import React, { useState, useRef, useEffect, useLayoutEffect, useContext, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { getAPICall, postAPICall, putAPICall } from "../../config/apiCalls";
import { UserContext } from "../../context/UserContext";
import StatusScheduleDialog from "../Projects/ProjectViewer/StatusScheduleDialog";
import { ProgressStackBar, fmtCurrency, fmtHours } from "../Projects/ProjectViewer/BudgetEditorTab";
import "../Projects/ProjectViewer/ProjectViewer.css"; // ProgressStackBar's styling (projectViewerBudgetProgress* classes) lives here
import ContactsCleanupTab from "./ContactsCleanupTab";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import ChecklistIcon from "@mui/icons-material/Checklist";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import EditIcon from "@mui/icons-material/Edit";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SearchIcon from "@mui/icons-material/Search";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import "./PMProjectDashboard.css";

// ── Icon helper ────────────────────────────────────────────────────────────────

const ICON_MAP = {
	account_balance_wallet: AccountBalanceWalletIcon,
	check_circle:           CheckCircleIcon,
	check:                  CheckIcon,
	checklist:              ChecklistIcon,
	chevron_right:          ChevronRightIcon,
	contact_mail:           ContactMailIcon,
	event_busy:             EventBusyIcon,
	expand_less:            ExpandLessIcon,
	expand_more:            ExpandMoreIcon,
	folder_open:            FolderOpenIcon,
	open_in_new:            OpenInNewIcon,
	request_quote:          RequestQuoteIcon,
	schedule:               ScheduleIcon,
	search:                 SearchIcon,
	trending_up:            TrendingUpIcon,
	visibility:             VisibilityIcon,
	work_history:           WorkHistoryIcon,
};

function Icon({ name, style }) {
	const Comp = ICON_MAP[name];
	return Comp ? <Comp style={{ fontSize: "inherit", display: "block", ...style }} /> : null;
}

// ── Dummy project data ─────────────────────────────────────────────────────────
// Project portfolio, action items, and budget watch are not yet wired to the API.

const CAPACITY = 40;
// Flat hours-per-weekday for chart capacity (projected % and chip colors) — weekdays in the
// period × this, regardless of employee schedule, holidays, or time off. Billable % itself is
// billable ÷ hours logged (billablePctOf), not a share of this capacity.
const WEEKDAY_HOURS = 8;

const DUMMY_PROJECTS = [
		{ id: "mcc", property: "Maple Court Condos", scope: "Roof Replacement", phase: "Construction Docs", office: "BOS",
			client: "Maple Court Condominium Trust", value: 184500, pct: 62, spent: 99000, start: "Mar 3, 2026",
			deadline: { date: "Jul 2, 2026", label: "100% CD set" },
			budget: ["green", "Under"], invoices: ["green", "All paid"], schedule: ["green", "On track"],
			contact: ["green", "Recent"], cam: ["green", "Recent"], time: ["green", "Active"], ai: "Low", amend: 2 },
		{ id: "gpz", property: "Granite Plaza", scope: "Plaza Waterproofing", phase: "Schematic Design", office: "BOS",
			client: "Granite Plaza Owners LLC", value: 96000, pct: 24, spent: 41000, start: "Feb 18, 2026",
			deadline: { date: "Jun 27, 2026", label: "SD review" },
			budget: ["red", "None"], invoices: ["yellow", "2 pending"], schedule: ["yellow", "Slipping"],
			contact: ["red", "None 21d"], cam: ["green", "Recent"], time: ["red", "None 9d"], ai: "High", amend: 1 },
		{ id: "bwa", property: "Birchwood Apartments", scope: "Balcony Inspection", phase: "Construction Docs", office: "BOS",
			client: "Birchwood Property Mgmt", value: 58200, pct: 78, spent: 45000, start: "Apr 7, 2026",
			deadline: { date: "Jul 18, 2026", label: "Field report" },
			budget: ["green", "On Budget"], invoices: ["green", "All paid"], schedule: ["green", "On track"],
			contact: ["green", "Recent"], cam: ["yellow", "Stale"], time: ["green", "Active"], ai: "Low", amend: 0 },
];

// ── Utilities ──────────────────────────────────────────────────────────────────

// Derive initials from a full "First Last" name string.
function nameInitials(name) {
	const parts = (name || "").trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "?";
	if (parts.length === 1) return parts[0][0].toUpperCase();
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function money(n) {
	if (n >= 1000) return "$" + (n / 1000).toFixed(n >= 100000 ? 0 : 1).replace(/\.0$/, "") + "k";
	return "$" + n;
}

function budgetInfo(p) {
	if (p.budget[1] === "None") return { hasBudget: false, spent: p.spent, total: 0, pct: 0, tone: "#9ca3af" };
	const pct = p.spent / p.value;
	const tone = pct > 1 ? "#c62828" : pct >= 0.9 ? "#ca8a04" : "#2e7d32";
	return { hasBudget: true, spent: p.spent, total: p.value, pct, tone };
}

function computeActionItems(portfolio, hubspotStatus) {
	const SEV_ORDER = { high: 0, med: 1, low: 2 };
	const items = [];

	for (const p of portfolio) {
		// No budget set → link to BQE budget tab for this project (phase BQE ID, same pattern as Project Viewer)
		if (!p.hasBudget) {
			const bqeUrl = p.id
				? `https://wap1-01.bqecore.com/webapp/Project/Edit/${encodeURIComponent(p.id)}?tab=budget`
				: null;
			items.push({
				sev: "high", project: p.name, icon: "account_balance_wallet",
				text: "No budget set", cta: "Set in BQE", href: bqeUrl,
			});
		}

		// No schedule → link to project viewer schedule tab
		if (!p.start || !p.end) {
			const text = !p.start && !p.end ? "No schedule set"
				: !p.start ? "Missing start date"
				: "Missing end date";
			items.push({
				sev: "med", project: p.name, icon: "event_busy",
				text, cta: "Set schedule",
				href: `/projects/viewer?recordId=${encodeURIComponent(p.id)}&tab=schedule`,
			});
		}

		// HubSpot contact checks (populated after async fetch)
		const hs = hubspotStatus[p.id];
		if (hs?.dealId) {
			if (hs.contactCount === 0) {
				items.push({
					sev: "med", project: p.name, icon: "contact_mail",
					text: "No contacts on deal", cta: "Add contact", href: hs.dealUrl,
				});
			} else if (hs.contactCount > 0 && hs.contactsWithoutName > 0) {
				items.push({
					sev: "low", project: p.name, icon: "contact_mail",
					text: `${hs.contactsWithoutName} contact${hs.contactsWithoutName > 1 ? "s" : ""} missing name`,
					cta: "Update contact", href: hs.dealUrl,
				});
			}
		}
	}

	return items.sort((a, b) => SEV_ORDER[a.sev] - SEV_ORDER[b.sev]);
}

function dummyBudgetWatch() {
	return DUMMY_PROJECTS
		.map(p => ({ property: p.property, ...budgetInfo(p) }))
		.filter(b => b.hasBudget)
		.sort((a, b) => b.pct - a.pct);
}


// ── Date helpers ───────────────────────────────────────────────────────────────

function toDateStr(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function addMonths(d, n) { const r = new Date(d); r.setMonth(r.getMonth() + n); return r; }
function mondayOf(d) {
	const r = new Date(d); r.setHours(0, 0, 0, 0);
	const day = r.getDay();
	r.setDate(r.getDate() - (day === 0 ? 6 : day - 1));
	return r;
}

// ── Name-matching helpers (ported from WorkloadViewer) ─────────────────────────
// Handles BQE "Last, First" format and token-based first+last matching.

const pmNormalizeName = (n) => {
	if (!n) return "";
	const s = n.trim();
	if (s.includes(",")) {
		const comma = s.indexOf(",");
		return `${s.slice(comma + 1).trim()} ${s.slice(0, comma).trim()}`.toLowerCase();
	}
	return s.toLowerCase();
};

const pmNameTokens = (n) => {
	const norm = pmNormalizeName(n)
		.replace(/[.]/g, " ").replace(/[^a-z\s'-]/g, " ").replace(/\s+/g, " ").trim();
	if (!norm) return [];
	return norm.split(" ")
		.map(t => t.replace(/^['-]+|['-]+$/g, ""))
		.filter(t => t && !["jr", "sr", "ii", "iii", "iv", "v"].includes(t));
};

const pmIsNameMatch = (a, b) => {
	const at = pmNameTokens(a);
	const bt = pmNameTokens(b);
	if (!at.length || !bt.length) return false;
	if (at.join(" ") === bt.join(" ")) return true;
	if (at.length >= 2 && bt.length >= 2) {
		return at[0] === bt[0] && at[at.length - 1] === bt[bt.length - 1];
	}
	return false;
};

// ── Employee matching ──────────────────────────────────────────────────────────

function entryMatchesEmployee(entry, emp) {
	if (emp?.bqe_id && entry?.resource_id) return entry.resource_id === emp.bqe_id;
	return pmIsNameMatch(entry?.employee, emp?.name);
}

// Mirrors WorkloadViewer isOwner: try name match, then bqe_id (don't short-circuit).
function budgetMatchesManager(b, emp) {
	const managerName = pmNormalizeName(b?.manager);
	if (managerName && pmIsNameMatch(emp?.name, b.manager)) return true;
	return !!(emp?.bqe_id && b?.manager_bqe_id && String(b.manager_bqe_id) === String(emp.bqe_id));
}

function budgetMatchesEmployee(b, emp) {
	if (emp?.bqe_id && b?.resource_id) return b.resource_id === emp.bqe_id;
	return pmIsNameMatch(b?.employee, emp?.name);
}

// ── Real KPI computation ───────────────────────────────────────────────────────

// Billable % = billable hours ÷ all hours logged (billable + non-billable).
const billablePctOf = (billable, nonbillable) => {
	const logged = (billable || 0) + (nonbillable || 0);
	return logged > 0 ? Math.round((billable || 0) / logged * 100) : 0;
};

function computeKPIs(timeEntries, budgets, employee, projRegistry, timeOffMap = {}, holidayList = []) {
	if (!employee) {
		return {
			count: 0, activeCount: 0, onHoldCount: 0,
			billablePct: 0, billableMonthLabel: "",
			billablePctMTD: 0, billableMTDLabel: "",
			missingBudget: 0, avgWeekly: 0,
		};
	}

	const now = new Date();

	// Billable % — share of the hours logged in a date range that were billable
	// (billable ÷ billable + non-billable), same as every other Billable % shown.
	const todayStr = toDateStr(now);

	const billablePctForRange = (startDate, endDate) => {
		const startStr = toDateStr(startDate);
		const endStr   = toDateStr(endDate);
		const entries  = timeEntries.filter(e => entryMatchesEmployee(e, employee) && e.date >= startStr && e.date <= endStr);
		return billablePctOf(
			entries.filter(e =>  e.billable).reduce((s, e) => s + (e.hours || 0), 0),
			entries.filter(e => !e.billable).reduce((s, e) => s + (e.hours || 0), 0),
		);
	};

	// Previous (fully completed) calendar month — rolls over automatically on the 1st.
	const prevMonthStartDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
	const prevMonthEndDate   = new Date(now.getFullYear(), now.getMonth(), 0);
	const billableMonthLabel = prevMonthStartDate.toLocaleString("en-US", { month: "long" });
	const billablePct        = billablePctForRange(prevMonthStartDate, prevMonthEndDate);

	// Current month, month-to-date (through today).
	const curMonthStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
	const billableMTDLabel  = `${now.toLocaleString("en-US", { month: "long" })} (MTD)`;
	const billablePctMTD    = billablePctForRange(curMonthStartDate, now);

	// Employee's personal project IDs — time entries by resource_id/name, plus budget allocations.
	// Mirrors WorkloadViewer's emp.projects (empProjectIds + empBudgets).
	const empProjectIds = new Set();
	timeEntries.forEach(entry => {
		if (entryMatchesEmployee(entry, employee) && entry.project_id) empProjectIds.add(entry.project_id);
	});
	budgets.forEach(b => {
		if (budgetMatchesEmployee(b, employee) && b.project_id) empProjectIds.add(b.project_id);
	});

	// isOwner: bqe_id first, name fallback — mirrors WorkloadViewer's isEmpOwner exactly
	const isOwner = (proj) => {
		if (proj.manager_bqe_id && employee.bqe_id) {
			return String(proj.manager_bqe_id) === String(employee.bqe_id);
		}
		const managerName = pmNormalizeName(proj.manager);
		return !!managerName && pmIsNameMatch(employee.name, proj.manager);
	};

	// Active owned projects: employee must have personal involvement AND be the owner.
	// active !== false matches WorkloadViewer's activePids filter.
	const activeOwnedIds = new Set(
		[...empProjectIds].filter(pid => {
			const proj = projRegistry.get(pid);
			return proj && proj.active !== false && isOwner(proj);
		})
	);
	const count       = activeOwnedIds.size;
	const onHoldCount = [...activeOwnedIds].filter(pid => {
		const proj = projRegistry.get(pid);
		return (proj.status || "").toLowerCase().trim() === "on hold";
	}).length;
	const activeCount = count - onHoldCount;

	// Missing budgets — active owned projects with no budget set (no rows or all zero)
	const missingBudget = [...activeOwnedIds].filter(pid => {
		const rows = budgets.filter(b => b.project_id === pid);
		return rows.length === 0 || rows.every(b => !b.budget_hours);
	}).length;

	// Average weekly hours — trailing 4 weeks
	const fourWeeksAgo  = toDateStr(addDays(now, -28));
	const recentEntries = timeEntries.filter(e => entryMatchesEmployee(e, employee) && e.date >= fourWeeksAgo && e.date <= todayStr);
	const avgWeekly     = recentEntries.reduce((s, e) => s + (e.hours || 0), 0) / 4;

	return { count, activeCount, onHoldCount, billablePct, billableMonthLabel, billablePctMTD, billableMTDLabel, missingBudget, avgWeekly };
}

// ── Real workload series ───────────────────────────────────────────────────────

function buildWorkloadSeries(timeEntries, budgets, employee, grain, timeOffMap = {}, holidayList = [], spentHours = []) {
	if (!employee) return [];
	const now    = new Date();
	const cap    = employee.hours_per_week || CAPACITY;
	const dayCap = Math.round((cap / 5) * 10) / 10;

	// Match WorkloadViewer's OR logic: resource-id lookup is primary; name-match is fallback only.
	// Using both simultaneously (as entryMatchesEmployee does) can double-count entries/budgets
	// that appear under both a resource_id and a name match.
	const bqeIdKey = employee.bqe_id ? String(employee.bqe_id) : null;
	const riMatchedEntries = bqeIdKey
		? timeEntries.filter(e => e.resource_id && String(e.resource_id) === bqeIdKey)
		: [];
	const empEntries = riMatchedEntries.length > 0
		? riMatchedEntries
		: timeEntries.filter(e => pmIsNameMatch(e.employee, employee.name));
	const riMatchedBudgets = bqeIdKey
		? budgets.filter(b => b.resource_id && String(b.resource_id) === bqeIdKey && b.budget_hours)
		: [];
	const empBudgets = riMatchedBudgets.length > 0
		? riMatchedBudgets
		: budgets.filter(b => pmIsNameMatch(b.employee, employee.name) && b.budget_hours);

	// Build sets of date strings for fast holiday/time-off lookup
	function expandPeriods(periods) {
		const set = new Set();
		for (const p of (periods || [])) {
			let cur = new Date(p.start + "T12:00:00");
			const end = new Date(p.end + "T12:00:00");
			while (cur <= end) { set.add(toDateStr(cur)); cur = addDays(cur, 1); }
		}
		return set;
	}
	const holidayDates = expandPeriods(holidayList);
	const timeOffDates = expandPeriods(timeOffMap[employee.name] || []);

	// Count business days in a range that are holidays vs time-off (holidays take priority)
	function countOffDays(startStr, endStr) {
		let holDays = 0, offDays = 0;
		let cur = new Date(startStr + "T12:00:00");
		const end = new Date(endStr + "T12:00:00");
		while (cur <= end) {
			const day = cur.getDay();
			if (day !== 0 && day !== 6) {
				const iso = toDateStr(cur);
				if (holidayDates.has(iso)) holDays++;
				else if (timeOffDates.has(iso)) offDays++;
			}
			cur = addDays(cur, 1);
		}
		return { holDays, offDays };
	}

	// Build per-day projected hours from budget allocations — Workload Viewer approach:
	// remaining budget (total allocated minus all-time personal spent) distributed evenly
	// across available future workdays (skipping weekends, holidays, time-off) to project end.
	const planByDay   = {};
	const todayDate   = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const spentById   = new Map();
	const spentByName = new Map();
	for (const s of spentHours) {
		if (s.resource_id && s.project_id) {
			const k = `${s.resource_id}|${s.project_id}`;
			spentById.set(k, (spentById.get(k) || 0) + s.total_hours);
		}
		if (s.employee && s.project_id) {
			const k = `${pmNormalizeName(s.employee)}|${s.project_id}`;
			spentByName.set(k, (spentByName.get(k) || 0) + s.total_hours);
		}
	}
	for (const b of empBudgets) {
		if (!b.end_date) continue;
		// Local midnight, same as WorkloadViewer's parseDate() — end_date itself is NOT projected
		const [pY, pM, pD] = b.end_date.split("-").map(Number);
		const projEnd = new Date(pY, pM - 1, pD);
		if (projEnd <= todayDate) continue;
		// WorkloadViewer requires a known project start — either an explicit project_start field
		// or at least one logged entry for the project. Skip budgets that meet neither condition
		// so the two pages compute identical projections.
		const projStart = b.project_start
			? new Date(b.project_start + "T12:00:00")
			: (() => {
				const first = empEntries
					.filter(e => e.project_id === b.project_id)
					.reduce((min, e) => (!min || e.date < min ? e.date : min), null);
				return first ? new Date(first + "T12:00:00") : null;
			})();
		if (!projStart) continue;
		const ridKey  = employee.bqe_id && b.project_id ? `${employee.bqe_id}|${b.project_id}` : null;
		const nameKey = employee.name   && b.project_id ? `${pmNormalizeName(employee.name)}|${b.project_id}` : null;
		const spent   = (ridKey && spentById.has(ridKey)) ? spentById.get(ridKey)
		              : (nameKey && spentByName.get(nameKey)) || 0;
		const remaining = b.budget_hours - spent;
		if (remaining <= 0.1) continue;
		// Project-wide on-hold window (only present on b when the phase is currently on
		// hold AND both dates are set — see workload.js) — excluded from this project's
		// projection, same rule as the Schedule tab and Workload Viewer.
		const onHoldStart = b.on_hold_start ? new Date(b.on_hold_start + "T12:00:00") : null;
		const onHoldEnd   = b.on_hold_end   ? new Date(b.on_hold_end   + "T12:00:00") : null;
		const isOnHoldDay = (day) => onHoldStart && onHoldEnd && day >= onHoldStart && day <= onHoldEnd;
		let availDays = 0;
		let d = new Date(todayDate);
		while (d < projEnd) {
			if (d.getDay() !== 0 && d.getDay() !== 6 && !isOnHoldDay(d)) {
				const iso = toDateStr(d);
				if (!holidayDates.has(iso) && !timeOffDates.has(iso)) availDays++;
			}
			d = addDays(d, 1);
		}
		if (availDays === 0) continue;
		const hPerDay = remaining / availDays;
		d = new Date(todayDate);
		while (d < projEnd) {
			if (d.getDay() !== 0 && d.getDay() !== 6 && !isOnHoldDay(d)) {
				const iso = toDateStr(d);
				if (!holidayDates.has(iso) && !timeOffDates.has(iso)) {
					planByDay[iso] = (planByDay[iso] || 0) + hPerDay;
				}
			}
			d = addDays(d, 1);
		}
	}

	function projectedHours(startStr, endStr) {
		let total = 0;
		let cur = new Date(startStr + "T12:00:00");
		const end = new Date(endStr + "T12:00:00");
		while (cur <= end) { total += planByDay[toDateStr(cur)] || 0; cur = addDays(cur, 1); }
		return total;
	}

	function sumHours(entries, startStr, endStr) {
		const slice = entries.filter(e => e.date >= startStr && e.date <= endStr);
		return {
			billable:    slice.filter(e =>  e.billable).reduce((s, e) => s + (e.hours || 0), 0),
			nonbillable: slice.filter(e => !e.billable).reduce((s, e) => s + (e.hours || 0), 0),
		};
	}

	// Chart starts at the fetch window boundary (first of month, 5 months back) so every weekday
	// including holidays is always visible, not just days that happen to have a time entry.
	const fromRaw      = addMonths(now, -5);
	const earliestEntry = new Date(fromRaw.getFullYear(), fromRaw.getMonth(), 1);
	const latestBudgetEnd  = empBudgets.reduce((latest, b) => {
		const end = b.end_date ? new Date(b.end_date) :
		            (b.project_start && b.duration_weeks) ? addDays(new Date(b.project_start), Math.round(b.duration_weeks * 7)) : null;
		return (end && end > latest) ? end : latest;
	}, addDays(now, 90));

	if (grain === "week") {
		const startWeek = mondayOf(earliestEntry);
		const endWeek   = mondayOf(latestBudgetEnd);
		const numWeeks  = Math.max(8, Math.round((endWeek - startWeek) / (7 * 86400000)) + 2);
		return Array.from({ length: numWeeks }, (_, i) => {
			const ws = addDays(startWeek, i * 7);
			const we = addDays(ws, 6);
			const wsStr = toDateStr(ws); const weStr = toDateStr(we);
			const isFuture = wsStr > toDateStr(now);
			const isToday  = !isFuture && weStr >= toDateStr(now);
			const label     = ws.toLocaleDateString("en-US", { month: "short", day: "numeric" });
			const dateRange = ws.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " – " +
			                  we.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
			if (isFuture) { const { holDays: fHol, offDays: fOff } = countOffDays(wsStr, weStr); return { label, dateRange, cap, billCap: WEEKDAY_HOURS * 5, future: true, isToday: false, types: { billable: 0, nonbillable: 0, timeoff: Math.round(fOff * dayCap * 10) / 10, holidays: Math.round(fHol * dayCap * 10) / 10, projected: projectedHours(wsStr, weStr) } }; }
			const { billable, nonbillable } = sumHours(empEntries, wsStr, weStr);
			const { holDays, offDays } = countOffDays(wsStr, weStr);
			return { label, dateRange, cap, billCap: WEEKDAY_HOURS * 5, future: false, isToday, types: { billable, nonbillable, timeoff: Math.round(offDays * dayCap * 10) / 10, holidays: Math.round(holDays * dayCap * 10) / 10, projected: isToday ? projectedHours(toDateStr(now), weStr) : 0 } };
		});
	}

	if (grain === "day") {
		// Every calendar day from earliest entry to latest project end, including weekends —
		// a weekend column just renders an empty bar when nothing was logged that day.
		const todayStr = toDateStr(now);
		const allDays = [];
		let cur = new Date(earliestEntry);
		while (cur <= latestBudgetEnd) {
			allDays.push(new Date(cur));
			cur = addDays(cur, 1);
		}
		return allDays.map((d, idx) => {
			const dStr      = toDateStr(d);
			const isWknd    = d.getDay() === 0 || d.getDay() === 6;
			const dCap      = isWknd ? 0 : dayCap;
			const dBillCap  = isWknd ? 0 : WEEKDAY_HOURS;
			const isFuture  = dStr > todayStr;
			const isToday   = dStr === todayStr;
			const prevDay   = idx > 0 ? allDays[idx - 1] : null;
			const showMonth = idx === 0 || !prevDay || d.getMonth() !== prevDay.getMonth();
			const label     = showMonth
				? d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
				: String(d.getDate());
			const sub       = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"][d.getDay()];
			const dateRange = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
			if (isFuture) { const isFutHol = holidayDates.has(dStr); const isFutOff = !isFutHol && timeOffDates.has(dStr); return { label, sub, iso: dStr, dateRange, cap: dCap, billCap: dBillCap, future: true, isToday: false, types: { billable: 0, nonbillable: 0, timeoff: isFutOff ? dCap : 0, holidays: isFutHol ? dCap : 0, projected: planByDay[dStr] || 0 } }; }
			const { billable, nonbillable } = sumHours(empEntries, dStr, dStr);
			const isHoliday = holidayDates.has(dStr);
			const isTimeOff = !isHoliday && timeOffDates.has(dStr);
			return { label, sub, iso: dStr, dateRange, cap: dCap, billCap: dBillCap, future: false, isToday, types: { billable, nonbillable, timeoff: isTimeOff ? dCap : 0, holidays: isHoliday ? dCap : 0, projected: isToday ? (planByDay[dStr] || 0) : 0 } };
		});
	}

	if (grain === "month") {
		const startMonth = new Date(earliestEntry.getFullYear(), earliestEntry.getMonth(), 1);
		const endMonth   = new Date(latestBudgetEnd.getFullYear(), latestBudgetEnd.getMonth(), 1);
		const numMonths  = Math.max(6,
			(endMonth.getFullYear() - startMonth.getFullYear()) * 12 +
			(endMonth.getMonth()   - startMonth.getMonth()) + 2
		);
		return Array.from({ length: numMonths }, (_, i) => {
			const md    = new Date(startMonth.getFullYear(), startMonth.getMonth() + i, 1);
			const me    = new Date(md.getFullYear(), md.getMonth() + 1, 0);
			const mdStr = toDateStr(md); const meStr = toDateStr(me);
			// Count actual business days for this month — matches WorkloadViewer's aggregateRange
			let bizDays = 0;
			for (let bd = new Date(md); bd <= me; bd = addDays(bd, 1)) {
				if (bd.getDay() !== 0 && bd.getDay() !== 6) bizDays++;
			}
			const monthCap = Math.round(bizDays * dayCap * 10) / 10;
			const billCap  = bizDays * WEEKDAY_HOURS;
			const isFuture = md.getFullYear() > now.getFullYear() || (md.getFullYear() === now.getFullYear() && md.getMonth() > now.getMonth());
			const isToday  = md.getMonth() === now.getMonth() && md.getFullYear() === now.getFullYear();
			const label     = md.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
			const dateRange = md.toLocaleDateString("en-US", { month: "long", year: "numeric" });
			if (isFuture) { const { holDays: fmHol, offDays: fmOff } = countOffDays(mdStr, meStr); return { label, dateRange, cap: monthCap, billCap, future: true, isToday: false, types: { billable: 0, nonbillable: 0, timeoff: Math.round(fmOff * dayCap * 10) / 10, holidays: Math.round(fmHol * dayCap * 10) / 10, projected: projectedHours(mdStr, meStr) } }; }
			const { billable, nonbillable } = sumHours(empEntries, mdStr, meStr);
			const { holDays: mHolDays, offDays: mOffDays } = countOffDays(mdStr, meStr);
			return { label, dateRange, cap: monthCap, billCap, future: false, isToday, types: { billable, nonbillable, timeoff: Math.round(mOffDays * dayCap * 10) / 10, holidays: Math.round(mHolDays * dayCap * 10) / 10, projected: isToday ? projectedHours(toDateStr(now), meStr) : 0 } };
		});
	}

	return [];
}

// ── Real budget watch computation ─────────────────────────────────────────────

// Share of a phase's budget used. Prefers the server's $ figures
// (classifyBudgetStatus, via /api/project-health/indicators) — the same basis
// as Project Health, the Project Viewer, and BQE's own "budget spent": all
// time at budget rates plus posted expenses, against services + expenses.
// Falls back to hours when the phase isn't in the indicators (they only cover
// active/on-hold phases) or they failed to load — callers hold off rendering
// a status while they're still loading, so it never flips from hours to $.
const budgetUsage = (indicator, budgetH, spentH) => {
	if (indicator?.budget_amount > 0 && indicator.spent_amount != null) {
		return { pct: indicator.spent_amount / indicator.budget_amount, basis: "amount", spent: indicator.spent_amount, total: indicator.budget_amount };
	}
	return budgetH > 0 ? { pct: spentH / budgetH, basis: "hours", spent: spentH, total: budgetH } : null;
};

// "$1,234 over ($5,678 spent of $4,444 budget)" / "12.5h remaining (…)"
const budgetUsageText = (usage) => {
	const fmt = usage.basis === "amount" ? fmtCurrency : (h) => `${fmtHours(h)}h`;
	const verb = usage.basis === "amount" ? "spent" : "logged";
	const variance = fmt(Math.abs(usage.total - usage.spent));
	const detail = `(${fmt(usage.spent)} ${verb} of ${fmt(usage.total)} budgeted)`;
	if (usage.pct > 1)    return `Over Budget — ${variance} over ${detail}`;
	if (usage.pct >= 0.9) return `Near Limit — ${variance} remaining ${detail}`;
	return `On Budget — ${variance} remaining ${detail}`;
};

// Collapses a phase status down to the tier Budget Watch cares about: "active"
// and "on_hold" are shown (on-hold under its own collapsed section), anything
// else (Completed, Inactive, Canceled) is dropped entirely.
const normalizeBudgetWatchStatus = (raw) => {
	const s = (raw || "").toLowerCase().trim();
	if (!s || s === "active") return "active";
	if (s === "on hold") return "on_hold";
	return "other";
};

function computeBudgetWatch(timeEntries, budgets, spentHours, employee, projRegistry, healthIndicators = {}) {
	if (!employee) return { active: [], onHold: [] };

	// Employee's personal project IDs (time entries + budget allocations)
	const empProjectIds = new Set();
	timeEntries.forEach(entry => {
		if (entryMatchesEmployee(entry, employee) && entry.project_id) empProjectIds.add(entry.project_id);
	});
	budgets.forEach(b => {
		if (budgetMatchesEmployee(b, employee) && b.project_id) empProjectIds.add(b.project_id);
	});

	// isOwner: bqe_id first, name fallback
	const isOwner = (proj) => {
		if (proj.manager_bqe_id && employee.bqe_id) return String(proj.manager_bqe_id) === String(employee.bqe_id);
		const managerName = pmNormalizeName(proj.manager);
		return !!managerName && pmIsNameMatch(employee.name, proj.manager);
	};

	// projRegistry.status is the raw phase_status string (shared across all the
	// compute* functions) — normalize to this function's active/on_hold/other
	// tier at comparison time instead of expecting it pre-normalized.
	const ownedIdsForStatus = (status) => new Set(
		[...empProjectIds].filter(pid => {
			const proj = projRegistry.get(pid);
			return proj && normalizeBudgetWatchStatus(proj.status) === status && isOwner(proj);
		})
	);

	const buildWatchList = (ownedIds) => {
		// Sum total budget hours per owned project across all team members' phases
		const byProject = {};
		for (const b of budgets) {
			if (!ownedIds.has(b.project_id) || !b.budget_hours) continue;
			if (!byProject[b.project_id]) byProject[b.project_id] = { name: b.project_name, bqeParent: b.bqe_parent || null, phaseName: b.phase_name || null, budget: 0 };
			if (b.bqe_parent  && !byProject[b.project_id].bqeParent)  byProject[b.project_id].bqeParent  = b.bqe_parent;
			if (b.phase_name  && !byProject[b.project_id].phaseName)   byProject[b.project_id].phaseName  = b.phase_name;
			byProject[b.project_id].budget += b.budget_hours;
		}

		// Every resource with a budget row on the project (regardless of hours
		// value) — the same "does this resource have a budget line" test the
		// Budget Editor tab uses, just derived from workload data here instead
		// of a single phase's live BQE fetch.
		const budgetedKeysByProject = {};
		for (const b of budgets) {
			if (!ownedIds.has(b.project_id)) continue;
			const key = b.resource_id || b.employee;
			if (!key) continue;
			if (!budgetedKeysByProject[b.project_id]) budgetedKeysByProject[b.project_id] = new Set();
			budgetedKeysByProject[b.project_id].add(key);
		}

		// All-time spent hours per project, split into hours logged by a
		// budgeted resource vs. a resource with no budget line at all.
		const inBudgetSpentByProject = {};
		const notInBudgetSpentByProject = {};
		for (const s of spentHours) {
			if (!s.project_id) continue;
			const key = s.resource_id || s.employee;
			const isBudgeted = key && budgetedKeysByProject[s.project_id]?.has(key);
			const bucket = isBudgeted ? inBudgetSpentByProject : notInBudgetSpentByProject;
			bucket[s.project_id] = (bucket[s.project_id] || 0) + s.total_hours;
		}

		return Object.entries(byProject)
			.map(([projectId, { name, bqeParent, phaseName, budget }]) => {
				const inBudgetSpent = inBudgetSpentByProject[projectId] || 0;
				const notInBudgetSpent = notInBudgetSpentByProject[projectId] || 0;
				const spent = inBudgetSpent + notInBudgetSpent;
				const usage = budgetUsage(healthIndicators[projectId], budget, spent);
				const pct   = usage ? usage.pct : 0;
				const tone  = pct > 1 ? "#c62828" : pct >= 0.9 ? "#ca8a04" : "#2e7d32";
				const fullName = (() => {
					const projectName = (name || "").trim();
					const phase       = (phaseName || "").trim();
					let middle = "";
					if (bqeParent) {
						const parent    = String(bqeParent).trim();
						const prefix    = projectName ? projectName + " | " : "";
						const stripped  = prefix && parent.startsWith(prefix) ? parent.slice(prefix.length) : parent;
						const pipeIdx   = stripped.lastIndexOf(" | ");
						const candidate = pipeIdx !== -1 ? stripped.slice(pipeIdx + 3) : stripped;
						if (candidate && candidate !== projectName) middle = candidate;
					}
					return [projectName, middle || null, phase || null].filter(Boolean).join(": ");
				})();
				return { projectId, property: fullName, spent, inBudgetSpent, notInBudgetSpent, total: budget, pct, tone, usage };
			})
			.filter(b => b.total > 0)
			.sort((a, b) => b.pct - a.pct);
	};

	return {
		active: buildWatchList(ownedIdsForStatus("active")),
		onHold: buildWatchList(ownedIdsForStatus("on_hold")),
	};
}

// ── Project portfolio computation ─────────────────────────────────────────────

// Portfolio views show both active and on-hold projects.
const isEligiblePhaseStatus = (status) =>
	["active", "on hold"].includes((status || "").toLowerCase().trim());

// Shared by computeKPIs/computeBudgetWatch/computeProjectPortfolio/
// computeContributorProjects — they all used to independently rebuild this
// same registry from scratch (4-5 redundant O(n) passes over the full
// timeEntries+budgets arrays on every data change). Built once per data
// change in the component body instead and passed in. `status` is kept as
// the raw phase_status string (not pre-normalized) so each consumer can
// apply its own eligibility rule against it — computeBudgetWatch in
// particular needs a 3-way tier (active/on_hold/other) rather than the
// boolean `active` the others use.
function buildProjectRegistry(timeEntries, budgets) {
	const projRegistry = new Map();
	timeEntries.forEach(entry => {
		if (!entry.project_id) return;
		if (!projRegistry.has(entry.project_id)) {
			projRegistry.set(entry.project_id, {
				active:         !entry.phase_status || isEligiblePhaseStatus(entry.phase_status),
				status:         entry.phase_status   || null,
				manager_bqe_id: entry.manager_bqe_id || null,
				manager:        entry.manager        || null,
				name:           entry.project_name   || null,
				phaseName:      entry.phase_name     || null,
				start:          entry.project_start  || null,
				end:            entry.end_date       || null,
			});
		}
		const reg = projRegistry.get(entry.project_id);
		if (entry.phase_status)   { reg.active = isEligiblePhaseStatus(entry.phase_status); reg.status = entry.phase_status; }
		if (entry.manager_bqe_id) reg.manager_bqe_id = entry.manager_bqe_id;
		if (entry.manager)        reg.manager        = entry.manager;
		if (entry.project_name)   reg.name           = entry.project_name;
		if (entry.phase_name)     reg.phaseName      = entry.phase_name;
	});
	budgets.forEach(b => {
		if (!b.project_id) return;
		if (!projRegistry.has(b.project_id)) {
			projRegistry.set(b.project_id, {
				active:         !b.phase_status || isEligiblePhaseStatus(b.phase_status),
				status:         b.phase_status   || null,
				manager_bqe_id: b.manager_bqe_id || null,
				manager:        b.manager        || null,
				name:           b.project_name   || null,
				phaseName:      b.phase_name     || null,
				start:          b.project_start  || null,
				end:            b.end_date       || null,
				onHoldStart:    b.on_hold_start  || null,
				onHoldEnd:      b.on_hold_end    || null,
			});
		}
		const reg = projRegistry.get(b.project_id);
		if (b.phase_status)   { reg.active = isEligiblePhaseStatus(b.phase_status); reg.status = b.phase_status; }
		if (b.manager_bqe_id) reg.manager_bqe_id = b.manager_bqe_id;
		if (b.manager)        reg.manager        = b.manager;
		if (b.project_name)   reg.name           = b.project_name;
		if (b.phase_name)     reg.phaseName      = b.phase_name;
		if (b.bqe_parent)     reg.bqeParent      = b.bqe_parent;
		if (b.project_start && (!reg.start || b.project_start < reg.start)) reg.start = b.project_start;
		if (b.end_date      && (!reg.end   || b.end_date      > reg.end))   reg.end   = b.end_date;
		if (b.on_hold_start)  reg.onHoldStart    = b.on_hold_start;
		if (b.on_hold_end)    reg.onHoldEnd      = b.on_hold_end;
	});
	return projRegistry;
}

function computeProjectPortfolio(timeEntries, budgets, spentHours, employee, projRegistry) {
	if (!employee) return [];

	// Employee's personal project IDs
	const empProjectIds = new Set();
	timeEntries.forEach(entry => {
		if (entryMatchesEmployee(entry, employee) && entry.project_id) empProjectIds.add(entry.project_id);
	});
	budgets.forEach(b => {
		if (budgetMatchesEmployee(b, employee) && b.project_id) empProjectIds.add(b.project_id);
	});

	// isOwner: bqe_id first, name fallback
	const isOwner = (proj) => {
		if (proj.manager_bqe_id && employee.bqe_id) return String(proj.manager_bqe_id) === String(employee.bqe_id);
		const managerName = pmNormalizeName(proj.manager);
		return !!managerName && pmIsNameMatch(employee.name, proj.manager);
	};

	// Active/on-hold owned project IDs
	const activeOwnedIds = new Set(
		[...empProjectIds].filter(pid => {
			const proj = projRegistry.get(pid);
			return proj && proj.active !== false && isOwner(proj);
		})
	);

	// Sum total budget hours per owned project across all team members
	const budgetByProject = {};
	for (const b of budgets) {
		if (!activeOwnedIds.has(b.project_id)) continue;
		budgetByProject[b.project_id] = (budgetByProject[b.project_id] || 0) + (b.budget_hours || 0);
	}

	// Spent hours per project
	const spentByProject = {};
	for (const s of spentHours) {
		if (!s.project_id) continue;
		spentByProject[s.project_id] = (spentByProject[s.project_id] || 0) + s.total_hours;
	}

	return [...activeOwnedIds]
		.map(pid => {
			const reg     = projRegistry.get(pid) || {};
			const budgetH = budgetByProject[pid] || 0;
			const spentH  = spentByProject[pid]  || 0;
			return {
				id:        pid,
				name:      reg.name      || "Unnamed Project",
				phaseName: reg.phaseName || "",
				status:    reg.status    || "",
				start:     reg.start     || null,
				end:       reg.end       || null,
				onHoldStart: reg.onHoldStart || null,
				onHoldEnd:   reg.onHoldEnd   || null,
				bqeParent: reg.bqeParent || null,
				budgetH,
				spentH,
				hasBudget: budgetH > 0,
			};
		})
		.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
}

// ── Contributor project computation ───────────────────────────────────────────
// Projects where the employee has logged time or has a budget allocation but is NOT the owner.

function computeContributorProjects(timeEntries, budgets, spentHours, employee, projRegistry) {
	if (!employee) return [];

	// Time entries are date-filtered (5 months back) — a reliable "recently active" signal.
	// Budgets are un-date-filtered (all-time), so budget-only projects may be old/inactive.
	const empTimeEntryProjectIds = new Set();
	timeEntries.forEach(entry => {
		if (entryMatchesEmployee(entry, employee) && entry.project_id) empTimeEntryProjectIds.add(entry.project_id);
	});

	const empProjectIds = new Set(empTimeEntryProjectIds);
	budgets.forEach(b => {
		if (budgetMatchesEmployee(b, employee) && b.project_id) empProjectIds.add(b.project_id);
	});

	// Projects with an explicit active/on-hold phase_status in any budget row
	const explicitlyEligibleProjects = new Set(
		budgets
			.filter(b => isEligiblePhaseStatus(b.phase_status))
			.map(b => b.project_id)
	);

	const isOwner = (proj) => {
		if (proj.manager_bqe_id && employee.bqe_id) return String(proj.manager_bqe_id) === String(employee.bqe_id);
		const managerName = pmNormalizeName(proj.manager);
		return !!managerName && pmIsNameMatch(employee.name, proj.manager);
	};

	// Contributor = NOT owner + active + personal involvement.
	// Budget-only projects (no recent time entries) must have an explicit active phase_status —
	// a null phase_status on an old budget row is not a reliable "active" signal.
	const contributorIds = new Set(
		[...empProjectIds].filter(pid => {
			const proj = projRegistry.get(pid);
			if (!proj || proj.active === false) return false;
			if (isOwner(proj)) return false;
			if (!empTimeEntryProjectIds.has(pid) && !explicitlyEligibleProjects.has(pid)) return false;
			return true;
		})
	);

	const budgetByProject = {};
	for (const b of budgets) {
		if (!contributorIds.has(b.project_id)) continue;
		budgetByProject[b.project_id] = (budgetByProject[b.project_id] || 0) + (b.budget_hours || 0);
	}

	const spentByProject = {};
	for (const s of spentHours) {
		if (!s.project_id) continue;
		spentByProject[s.project_id] = (spentByProject[s.project_id] || 0) + s.total_hours;
	}

	return [...contributorIds]
		.map(pid => {
			const reg     = projRegistry.get(pid) || {};
			const budgetH = budgetByProject[pid] || 0;
			const spentH  = spentByProject[pid]  || 0;
			return {
				id:        pid,
				name:      reg.name      || "Unnamed Project",
				phaseName: reg.phaseName || "",
				status:    reg.status    || "",
				start:     reg.start     || null,
				end:       reg.end       || null,
				onHoldStart: reg.onHoldStart || null,
				onHoldEnd:   reg.onHoldEnd   || null,
				bqeParent: reg.bqeParent || null,
				budgetH,
				spentH,
				hasBudget: budgetH > 0,
			};
		})
		.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
}

// ── Health / risk constants ────────────────────────────────────────────────────

const HEALTH = {
	green:   { dot: "#2e7d32", bg: "#e8f5e9", label: "Healthy" },
	yellow:  { dot: "#ca8a04", bg: "#fffde7", label: "Watch" },
	red:     { dot: "#c62828", bg: "#ffebee", label: "At risk" },
	missing: { dot: "#9ca3af", bg: "#f5f5f5", label: "No data" },
};
const RISK_MAP = { Low: "green", Medium: "yellow", High: "red" };

// ── Avatar ─────────────────────────────────────────────────────────────────────
// employee shape from API: { id, name, office, job_title_id, ... }

function Avatar({ employee, size = 36 }) {
	return (
		<span className="pmAvatar" style={{ width: size, height: size, minWidth: size, fontSize: Math.round(size * 0.38) }}>
			{nameInitials(employee?.name)}
		</span>
	);
}

// ── Stat card ──────────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, sub, value2, sub2, tone, tone2, accent }) {
	const tones = {
		blue:   { ic: "#184c93", icbg: "#e7eef7" },
		green:  { ic: "#2e7d32", icbg: "#e8f5e9" },
		orange: { ic: "#c2620f", icbg: "#fff1e2" },
		red:    { ic: "#c62828", icbg: "#ffebee" },
		slate:  { ic: "#566472", icbg: "#eef1f4" },
	};
	const t  = tones[tone]  || tones.slate;
	const t2 = tones[tone2] || t;
	if (value2 != null) {
		return (
			<div className="pmStatCard" style={{ borderTop: `3px solid ${accent ? t.ic : "transparent"}` }}>
				<span className="pmStatCard-iconWrap" style={{ background: t.icbg, color: t.ic }}>
					<Icon name={icon} style={{ fontSize: 16 }} />
				</span>
				<span className="pmStatCard-label">{label}</span>
				<div className="pmStatCard-value">
					<span className="pmStatCard-num" style={{ color: t.ic }}>{value}</span>
					{sub && <span className="pmStatCard-sub">{sub}</span>}
					<span className="pmStatCard-sep">/</span>
					<span className="pmStatCard-num" style={{ color: t2.ic }}>{value2}</span>
					{sub2 && <span className="pmStatCard-sub">{sub2}</span>}
				</div>
			</div>
		);
	}
	return (
		<div className="pmStatCard" style={{ borderTop: `3px solid ${accent ? t.ic : "transparent"}` }}>
			<span className="pmStatCard-iconWrap" style={{ background: t.icbg, color: t.ic }}>
				<Icon name={icon} style={{ fontSize: 16 }} />
			</span>
			<span className="pmStatCard-label">{label}</span>
			<div className="pmStatCard-value">
				<span className="pmStatCard-num" style={{ color: tone === "red" && value !== "0" ? "#c62828" : undefined }}>
					{value}
				</span>
				{sub && <span className="pmStatCard-sub">{sub}</span>}
			</div>
		</div>
	);
}

// ── Section card ───────────────────────────────────────────────────────────────

function SectionCard({ title, icon, count, titleAfter, action, children, bodyStyle }) {
	return (
		<div className="pmSectionCard">
			<div className="pmSectionCard-header">
				<span className="pmSectionCard-icon"><Icon name={icon} style={{ fontSize: 19 }} /></span>
				<span className="pmSectionCard-title">{title}</span>
				{count != null && <span className="pmSectionCard-count">{count}</span>}
				{titleAfter}
				<span style={{ flex: 1 }} />
				{action}
			</div>
			<div className="pmSectionCard-body" style={bodyStyle}>{children}</div>
		</div>
	);
}

// ── Health dot ─────────────────────────────────────────────────────────────────

function HealthDot({ value, showLabel }) {
	const [c, label] = value;
	const h = HEALTH[c] || HEALTH.missing;
	return (
		<span className="pmHealthDot">
			<span className="pmHealthDot-dot" style={{ background: h.dot }} />
			{showLabel && <span style={{ fontSize: 12, color: "rgba(0,0,0,0.7)", fontWeight: 500 }}>{label}</span>}
		</span>
	);
}

// ── Workload chart ─────────────────────────────────────────────────────────────

const WL_COLORS = {
	billable:    "#43a047",
	nonbillable: "#ef5350",
	projected:   "#5b8dd6",
	timeoff:     "#9575cd",
	holidays:    "#f59e0b",
};
const WL_STACK  = ["billable", "nonbillable", "timeoff", "holidays", "projected"];
const WL_LEGEND = [["billable", "Billable"], ["nonbillable", "Non-Billable"], ["timeoff", "Time Off"], ["holidays", "Holidays"], ["projected", "Projected"]];

const UTIL_UNDER      = 0.65;
const UTIL_OVER       = 0.80;
const UTIL_OVERLOADED = 0.90;
const UTIL_STYLES = {
	under:      { bg: "#dbeafe", border: "#3b82f6", text: "#000000" },
	onTrack:    { bg: "#dcfce7", border: "#22c55e", text: "#000000" },
	over:       { bg: "#ffedd5", border: "#f97316", text: "#000000" },
	overloaded: { bg: "#fee2e2", border: "#ef4444", text: "#000000" },
};
const getUtilStyle = (projected, capacity) => {
	if (projected < 0.05 || capacity === 0) return null;
	const u = projected / capacity;
	if (u > UTIL_OVERLOADED) return UTIL_STYLES.overloaded;
	if (u > UTIL_OVER)       return UTIL_STYLES.over;
	if (u >= UTIL_UNDER)     return UTIL_STYLES.onTrack;
	return UTIL_STYLES.under;
};

function WorkloadChart({ series, grain }) {
	const chartH     = 180;
	const MIN_SLOT   = grain === "week" ? 90 : grain === "month" ? 50 : 38;
	const projStripe = `repeating-linear-gradient(45deg, ${WL_COLORS.projected}, ${WL_COLORS.projected} 4px, #79a1de 4px, #79a1de 8px)`;

	// Tiered bar heights: 4 levels at 25/50/75/100% of maxH, with 12px top padding so bars
	// never touch the today line. Thresholds are grain-aware: day (0–8h), week (0–40h), month (0–180h).
	const maxH = chartH - 24;
	const tieredBarH = (hours) => {
		if (hours < 0.05) return 0;
		if (grain === "day") {
			if (hours < 2)  return Math.round(maxH * 0.25);
			if (hours < 4)  return Math.round(maxH * 0.50);
			if (hours < 6)  return Math.round(maxH * 0.75);
			return maxH;
		}
		if (grain === "month") {
			if (hours < 40)  return Math.round(maxH * 0.25);
			if (hours < 80)  return Math.round(maxH * 0.50);
			if (hours < 120) return Math.round(maxH * 0.75);
			return maxH;
		}
		// week
		if (hours < 10)   return Math.round(maxH * 0.25);
		if (hours < 20)   return Math.round(maxH * 0.50);
		if (hours < 30)   return Math.round(maxH * 0.75);
		return maxH;
	};
	const todayIdx   = series.findIndex(s => s.isToday);
	const scrollRef  = useRef(null);

	// Scroll so today is visible (roughly centered) after the series loads or grain changes
	useLayoutEffect(() => {
		const el = scrollRef.current;
		if (!el || todayIdx < 0) return;
		const slotWidth = el.scrollWidth / series.length;
		el.scrollLeft = Math.max(0, todayIdx * slotWidth - el.clientWidth / 2 + slotWidth / 2);
	}, [series.length, todayIdx]); // eslint-disable-line react-hooks/exhaustive-deps

	// In day grain, group future slots by ISO week to render one projected bar per week
	const weekProjections = useMemo(() => {
		if (grain !== "day") return [];
		const groups = [];
		let i = 0;
		while (i < series.length) {
			const s = series[i];
			if (!s.iso) { i++; continue; }
			const monday      = mondayOf(new Date(s.iso + "T12:00:00"));
			const weekStartIso = toDateStr(monday);
			const sunday       = addDays(monday, 6);
			let j = i;
			let projected = 0;
			while (j < series.length && series[j].iso) {
				if (toDateStr(mondayOf(new Date(series[j].iso + "T12:00:00"))) !== weekStartIso) break;
				projected += series[j].types.projected || 0;
				j++;
			}
			if (projected > 0.05) {
				const crossMonth = monday.getMonth() !== sunday.getMonth();
				const fmtS = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
				const dateRange = crossMonth
					? `${fmtS(monday)} – ${fmtS(sunday)}`
					: `${monday.toLocaleDateString("en-US", { month: "short" })} ${monday.getDate()}–${sunday.getDate()}`;
				groups.push({ startIdx: i, count: j - i, projected, dateRange });
			}
			i = j === i ? i + 1 : j;
		}
		return groups;
	}, [series, grain]); // eslint-disable-line react-hooks/exhaustive-deps

	// In day grain, projected is rendered as week-spanning bars — exclude from per-slot stack
	const barStack = grain === "day" ? WL_STACK.filter(k => k !== "projected") : WL_STACK;

	const now = new Date();
	const todayLineLeft = (() => {
		if (grain === "day") return "0%";
		if (grain === "week") {
			const dow = (now.getDay() + 6) % 7; // 0=Mon … 6=Sun
			return `${(dow / 7 * 100).toFixed(1)}%`;
		}
		const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
		return `${((now.getDate() - 1) / daysInMonth * 100).toFixed(1)}%`;
	})();

	return (
		<div className="pmWorkloadChart">
<div ref={scrollRef} style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
				<div style={{ display: "flex", minWidth: "100%", flexDirection: "column" }}>

					{/* Date header */}
					<div style={{ display: "flex", borderBottom: "2px solid #e3e6eb" }}>
						{series.map((s, i) => {
							const isWeek = grain === "week";
							const headerText = isWeek
								? s.dateRange.replace(/,\s*\d{4}$/, "")
								: s.label;
							return (
								<div key={i} style={{
									flex: 1, minWidth: MIN_SLOT,
									display: "flex", flexDirection: "column",
									alignItems: grain === "day" ? "center" : "flex-start",
									paddingLeft: grain === "day" ? 0 : 5,
									paddingTop: isWeek ? 5 : 3, paddingBottom: isWeek ? 5 : 3,
									borderLeft: i > 0 ? "1px solid #e3e6eb" : "none",
									overflow: "hidden", boxSizing: "border-box",
									background: s.isToday ? "rgba(24,76,147,0.07)" : "#f4f6f9",
								}}>
									<span style={{ fontSize: isWeek ? 10 : 9, fontWeight: 600, color: s.isToday ? "#184c93" : "#3a4051", whiteSpace: "nowrap" }}>
										{headerText}
									</span>
									{s.sub && <span style={{ fontSize: 8, color: s.isToday ? "#184c93" : "#9ca3af" }}>{s.sub}</span>}
								</div>
							);
						})}
					</div>

					{/* Chart + capacity line */}
					<div style={{ display: "flex", alignItems: "flex-end", height: chartH, position: "relative", marginTop: 8 }}>
						{/* Week projected overlays — absolutely positioned inside chart area behind regular bars */}
						{weekProjections.map(g => {
							const projBarH = tieredBarH(g.projected);
							if (projBarH === 0) return null;
							const weekNetCap = series.slice(g.startIdx, g.startIdx + g.count).reduce((sum, slot) =>
								sum + (slot.billCap || 0), 0);
							const us         = getUtilStyle(g.projected, weekNetCap);
							const pct        = weekNetCap > 0 ? Math.max(1, Math.round(g.projected / weekNetCap * 100)) : 0;
							const tip = (
								<div style={{ fontSize: 12 }}>
									<div style={{ fontWeight: 700, marginBottom: 3 }}>Planned</div>
									<div style={{ color: "#ccc", marginBottom: 3 }}>{g.dateRange}</div>
									<div style={{ color: "#90b4e8" }}>Planned: <strong>{g.projected.toFixed(1)}h</strong></div>
									{weekNetCap > 0 && pct > 0 && <div style={{ color: "#90b4e8" }}>Projected billable %: <strong>{pct}%</strong></div>}
								</div>
							);
							const barLeft  = g.startIdx * MIN_SLOT + 2;
							const barWidth = g.count * MIN_SLOT - 4;
							return (
								<React.Fragment key={`wgp${g.startIdx}`}>
									{/* Bar — zIndex 1, behind day slots (zIndex 5); height = bar only so hover passes through above it */}
									<Tooltip title={tip} arrow placement="top">
										<div style={{ position: "absolute", left: barLeft, width: barWidth, bottom: 0, height: projBarH, zIndex: 1, cursor: "default", pointerEvents: "all", background: "#5b8dd6", backgroundImage: projStripe, borderRadius: "3px 3px 0 0" }} />
									</Tooltip>
									{/* Chip — separate element above everything, no pointer events */}
									{us && (
										<div style={{ position: "absolute", left: barLeft + barWidth / 2, bottom: projBarH + 4, transform: "translateX(-50%)", padding: "1px 6px", background: us.bg, border: `1px solid ${us.border}`, borderRadius: 8, fontSize: 11, fontWeight: 700, color: us.text, whiteSpace: "nowrap", zIndex: 15, fontVariantNumeric: "tabular-nums", userSelect: "none", pointerEvents: "none" }}>
											{g.projected.toFixed(1)}h / {pct}%
										</div>
									)}
								</React.Fragment>
							);
						})}
{series.map((s, i) => {
							const hasStatusLine = s.isToday || s.future;
							const tip = (
								<div style={{ fontSize: 12 }}>
									<div style={{ fontWeight: 700, marginBottom: hasStatusLine ? 1 : 3 }}>{s.dateRange || s.label}</div>
									{s.isToday && <div style={{ color: "#aaa", marginBottom: 3, fontSize: 11 }}>Today</div>}
									{!s.isToday && s.future && <div style={{ color: "#aaa", marginBottom: 3, fontSize: 11 }}>Projected</div>}
									{s.types.billable    > 0 && <div style={{ color: "#81c784" }}>Billable: <strong>{s.types.billable.toFixed(1)}h</strong></div>}
									{s.types.nonbillable > 0 && <div style={{ color: WL_COLORS.nonbillable }}>Non-billable: <strong>{s.types.nonbillable.toFixed(1)}h</strong></div>}
									{grain !== "day" && s.types.projected > 0 && <div style={{ color: "#90b4e8" }}>Planned: <strong>{s.types.projected.toFixed(1)}h</strong></div>}
									{s.types.timeoff     > 0 && <div style={{ color: "#ce93d8" }}>Off: <strong>{s.types.timeoff.toFixed(1)}h</strong></div>}
									{s.types.holidays    > 0 && <div style={{ color: "#fbbf24" }}>Holiday: <strong>{s.types.holidays.toFixed(0)}h</strong></div>}
									{!s.future && s.types.billable > 0 &&
										<div>Billable %: <strong>{billablePctOf(s.types.billable, s.types.nonbillable)}%</strong></div>}
									{grain !== "day" && s.billCap > 0 && s.types.projected > 0 &&
										<div style={{ color: "#90b4e8" }}>Projected billable %: <strong>{Math.max(1, Math.round(s.types.projected / s.billCap * 100))}%</strong></div>}
								</div>
							);
						{/* Day grain: slot is pointer-event-transparent so hover reaches projected bar below;
							    Tooltip lives only on the bar div. Week/month: Tooltip wraps full slot. */}
							const slotDiv = (
								<div style={{ flex: 1, minWidth: MIN_SLOT, height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "stretch", position: "relative", zIndex: grain === "day" ? 5 : 2, cursor: "default",
									pointerEvents: grain === "day" ? "none" : "auto",
									borderLeft: i > 0 ? "1px dashed #e3e6eb" : "none",
								}}>
									{/* Today divider inside this slot — always correctly placed */}
									{s.isToday && (
										<div style={{ position: "absolute", top: 0, bottom: 0, left: todayLineLeft, borderLeft: "2px dotted #184c93", zIndex: 4, pointerEvents: "none" }}>
											<span className="pmTodayBadge">TODAY</span>
										</div>
									)}
									{/* Projected utilization chip — week and month grain only */}
									{grain !== "day" && s.future && s.types.projected > 0 && (() => {
										const netCap = s.billCap || 0;
										const us = getUtilStyle(s.types.projected, netCap);
										if (!us) return null;
										const pct = Math.max(1, Math.round(s.types.projected / netCap * 100));
										return (
											<div style={{ position: "absolute", left: "50%", top: 6, transform: "translateX(-50%)", padding: "1px 6px", background: us.bg, border: `1px solid ${us.border}`, borderRadius: 8, fontSize: 11, fontWeight: 700, color: us.text, whiteSpace: "nowrap", zIndex: 3, fontVariantNumeric: "tabular-nums", userSelect: "none", pointerEvents: "none" }}>
												{s.types.projected.toFixed(1)}h / {pct}%
											</div>
										);
									})()}
									{/* Billable utilization chip — past/today, week and month grain only */}
									{grain !== "day" && !s.future && s.types.billable > 0 && (() => {
										const netCap = s.billCap || 0;
										const us = getUtilStyle(s.types.billable, netCap);
										if (!us) return null;
										const pct = Math.max(1, billablePctOf(s.types.billable, s.types.nonbillable));
										return (
											<div style={{ position: "absolute", left: "50%", top: 6, transform: "translateX(-50%)", padding: "1px 6px", background: us.bg, border: `1px solid ${us.border}`, borderRadius: 8, fontSize: 11, fontWeight: 700, color: us.text, whiteSpace: "nowrap", zIndex: 3, fontVariantNumeric: "tabular-nums", userSelect: "none", pointerEvents: "none" }}>
												{s.types.billable.toFixed(1)}h / {pct}%
											</div>
										);
									})()}
									{/* Stacked color bar */}
									{(() => {
										const total = barStack.reduce((a, k) => a + (s.types[k] || 0), 0);
										const barH  = tieredBarH(total);
										if (barH === 0) return null;
										const barDiv = (
											<div style={{ margin: "0 2px", display: "flex", flexDirection: "column-reverse", borderRadius: "3px 3px 0 0", overflow: "hidden", pointerEvents: grain === "day" ? "auto" : "none" }}>
												{barStack.map(k => (s.types[k] || 0) > 0 ? (
													<div key={k} style={{ height: total > 0 ? ((s.types[k] || 0) / total) * barH : 0, background: k === "projected" ? projStripe : WL_COLORS[k], flexShrink: 0 }} />
												) : null)}
											</div>
										);
										return grain === "day"
											? <Tooltip title={tip} arrow placement="top">{barDiv}</Tooltip>
											: barDiv;
									})()}
								</div>
							);
							return grain === "day"
								? <React.Fragment key={i}>{slotDiv}</React.Fragment>
								: <Tooltip key={i} title={tip} arrow placement="top">{slotDiv}</Tooltip>;
						})}
					</div>


				</div>
			</div>

			{/* Legend */}
			<div className="pmWorkloadChart-legend">
				{WL_LEGEND.map(([k, l]) => (
					<span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
						<span style={{ width: 12, height: 10, borderRadius: 3, background: k === "projected" ? projStripe : WL_COLORS[k], display: "inline-block", flexShrink: 0 }} />
						{l}
					</span>
				))}
				{[
					{ bg: UTIL_STYLES.under.bg,      border: UTIL_STYLES.under.border,      label: "Under target (<65%)" },
					{ bg: UTIL_STYLES.onTrack.bg,    border: UTIL_STYLES.onTrack.border,    label: "On track (65–80%)" },
					{ bg: UTIL_STYLES.over.bg,       border: UTIL_STYLES.over.border,       label: "Above target (80–90%)" },
					{ bg: UTIL_STYLES.overloaded.bg, border: UTIL_STYLES.overloaded.border, label: "Overloaded (>90%)" },
				].map(({ bg, border, label }) => (
					<span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
						<span style={{ width: 12, height: 10, borderRadius: 3, background: bg, border: `1px solid ${border}`, display: "inline-block", flexShrink: 0 }} />
						{label}
					</span>
				))}
			</div>
		</div>
	);
}

// ── Segmented control ──────────────────────────────────────────────────────────

function Segmented({ value, options, onChange }) {
	return (
		<div className="pmSegmented">
			{options.map(o => (
				<button
					key={o}
					className={`pmSegmented-btn${o.toLowerCase() === value ? " pmSegmented-btn--active" : ""}`}
					onClick={() => onChange(o.toLowerCase())}
				>
					{o}
				</button>
			))}
		</div>
	);
}

// ── Project health metric chips ─────────────────────────────────────────────────
// Same color semantics, values, and hover tooltips as the Project Health Dashboard's
// Amendments/Contact/CompanyCam/Time Entries columns — sourced from the same APIs.

const HEALTH_COLORS = { green: "#2e7d32", red: "#c62828", yellow: "#ca8a04" };
const HEALTH_BG     = { green: "#e8f5e9", red: "#ffebee",  yellow: "#fffde7" };

function HealthChip({ status, label, tooltip }) {
	return (
		<Tooltip title={tooltip} arrow placement="top">
			<span className="pmHealthChip" style={{ background: HEALTH_BG[status], color: HEALTH_COLORS[status] }}>
				{label}
			</span>
		</Tooltip>
	);
}

function AmendmentsChip({ submittedCount, pendingCount }) {
	const tip = pendingCount > 0
		? `${submittedCount} submitted, ${pendingCount} pending`
		: submittedCount > 0
			? `${submittedCount} submitted amendment${submittedCount !== 1 ? "s" : ""}`
			: "No amendments";
	return (
		<Tooltip title={tip} arrow placement="top">
			<span className="pmHealthChip pmHealthChip--neutral">{submittedCount}</span>
		</Tooltip>
	);
}

function ContactChip({ hasRecentContact }) {
	if (hasRecentContact === null || hasRecentContact === undefined) return <span className="pmHealthChip pmHealthChip--muted">—</span>;
	return hasRecentContact
		? <HealthChip status="green" label="Recent" tooltip="Email or call logged in last 7 days" />
		: <HealthChip status="red"   label="None"   tooltip="No contact in last 7 days" />;
}

function CompanyCamChip({ hasRecentPhotos, recentPhotoCount, allTimePhotoCount }) {
	if (hasRecentPhotos === null || hasRecentPhotos === undefined) return <span className="pmHealthChip pmHealthChip--muted">—</span>;
	const recent  = recentPhotoCount   ?? 0;
	const allTime = allTimePhotoCount  ?? 0;
	const label   = `${recent} - ${allTime}`;
	const tooltip = `${recent} photos in the last 7 days / ${allTime} photos all time`;
	return hasRecentPhotos
		? <HealthChip status="green"  label={label} tooltip={tooltip} />
		: <HealthChip status="yellow" label={label} tooltip={tooltip} />;
}

function TimeEntriesChip({ hasRecentTimeEntries }) {
	if (hasRecentTimeEntries === null || hasRecentTimeEntries === undefined) return <span className="pmHealthChip pmHealthChip--muted">—</span>;
	return hasRecentTimeEntries
		? <HealthChip status="green" label="Active" tooltip="Time logged in the last 7 days" />
		: <HealthChip status="red"   label="None"   tooltip="No time entries in the last 7 days" />;
}

// ── Project portfolio table header ──────────────────────────────────────────────

const PORTFOLIO_HEADERS = [
	{ label: "PROJECT" },
	{ label: "STATUS",  center: true },
	{ label: "BUDGET" },
	{ label: "AMEND",   center: true, tooltip: "Amendments" },
	{ label: "CONTACT", center: true },
	{ label: "CCAM",    center: true, tooltip: "CompanyCam" },
	{ label: "TIME",    center: true, tooltip: "Time Entries" },
	{ label: "" },
	{ label: "" },
	{ label: "" },
];

// ── Project portfolio row ──────────────────────────────────────────────────────

function ProjectRow({ p, hs = {}, health = {}, budgetLoading = false, last, onUpdated, onCompleted }) {
	const { instance, accounts } = useMsal();
	const loggedInId = useContext(UserContext);
	const [open, setOpen] = useState(false);
	const usage = p.hasBudget && !budgetLoading ? budgetUsage(health, p.budgetH, p.spentH) : null;
	const awaitingBudget = p.hasBudget && budgetLoading;
	const pct = usage ? usage.pct : null;

	const hubspotUrl    = hs.dealUrl || null;
	const companyCamUrl = hs.companyCamUrl || null;
	const dropboxUrl    = hs.dropboxUrl || null;
	const bqeUrl        = p.id ? `https://wap1-01.bqecore.com/webapp/Project/Edit/${encodeURIComponent(p.id)}` : null;

	// Status + Schedule editor — same dialog/server endpoints as the Project Viewer's
	// edit buttons. Saves write straight to BQE; onUpdated patches this row's data
	// immediately from the confirmed write rather than waiting on the next data refresh.
	const [editDialogOpen, setEditDialogOpen] = useState(false);

	const handleSaveStatus = async (statusKey) => {
		if (!p.id) throw new Error("No BQE phase ID available.");
		const res = await putAPICall(instance, accounts[0], "/api/bqe/phase/status", {
			bqePhaseId: p.id,
			status: statusKey,
		});
		onUpdated?.({ status: res?.data?.status || statusKey });
	};

	const handleSaveSchedule = async (startDate, durationWeeks, endDate) => {
		if (!p.id) throw new Error("No BQE phase ID available.");
		await putAPICall(instance, accounts[0], "/api/bqe/phase/schedule", {
			bqePhaseId: p.id,
			...(startDate !== null && startDate !== undefined && { startDate }),
			...(durationWeeks !== null && durationWeeks !== undefined && { durationWeeks }),
			...(endDate !== null && endDate !== undefined && { endDate }),
		});
		onUpdated?.({ start: startDate || "", end: endDate || "" });
	};

	const handleSaveOnHoldSchedule = async (startDate, durationWeeks, endDate) => {
		if (!p.id) throw new Error("No BQE phase ID available.");
		await putAPICall(instance, accounts[0], "/api/bqe/phase/schedule", {
			bqePhaseId: p.id,
			...(startDate !== undefined && { onHoldStartDate: startDate }),
			...(durationWeeks !== undefined && { onHoldDurationWeeks: durationWeeks }),
			...(endDate !== undefined && { onHoldEndDate: endDate }),
		});
		onUpdated?.({ onHoldStart: startDate || "", onHoldEnd: endDate || "" });
	};

	const handleLogSaveFailure = async (message) => {
		try {
			await postAPICall(instance, accounts[0], "/api/logs/automation/add", {
				employeeID: loggedInId,
				message: `PM Project Dashboard status/schedule save failed (project ${p.id}): ${message}`,
				success: false,
				tool_name: "PM Project Dashboard",
			});
		} catch (err) {
			console.error("[PMDashboard] failed to write automation log", err);
		}
	};

	// Project Status Update — free-text note stored on the HubSpot deal. Loaded lazily
	// the first time the row is expanded, shown read-only, and edited in place via the
	// "Edit" button — written straight back to HubSpot on save.
	const dealId = hs.dealId || null;
	const [statusText, setStatusText]         = useState("");
	const [statusDraft, setStatusDraft]       = useState("");
	const [isEditingStatus, setIsEditingStatus] = useState(false);
	const [statusLoaded, setStatusLoaded]     = useState(false);
	const [statusLoading, setStatusLoading]   = useState(false);
	const [statusSaving, setStatusSaving]     = useState(false);
	const [statusError, setStatusError]       = useState("");
	const [statusSavedAt, setStatusSavedAt]   = useState(0);
	const savedTimeoutRef = useRef(null);

	useEffect(() => () => clearTimeout(savedTimeoutRef.current), []);

	useEffect(() => {
		if (!open || !dealId || statusLoaded) return;
		let cancelled = false;
		setStatusLoading(true);
		setStatusError("");
		getAPICall(instance, accounts[0], "/api/hubspot/projectStatusUpdate", { dealID: dealId })
			.then(res => { if (!cancelled) setStatusText(res.data?.value || ""); })
			.catch(() => { if (!cancelled) setStatusError("Failed to load status update."); })
			.finally(() => { if (!cancelled) { setStatusLoading(false); setStatusLoaded(true); } });
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, dealId, statusLoaded]);

	const startEditingStatus = () => {
		setStatusDraft(statusText);
		setStatusError("");
		setIsEditingStatus(true);
	};

	const cancelEditingStatus = () => {
		setIsEditingStatus(false);
		setStatusError("");
	};

	const saveStatusUpdate = () => {
		if (!dealId || statusSaving) return;
		setStatusSaving(true);
		setStatusError("");
		postAPICall(instance, accounts[0], "/api/hubspot/projectStatusUpdate", { dealID: dealId, value: statusDraft })
			.then(() => {
				setStatusText(statusDraft);
				setIsEditingStatus(false);
				setStatusSavedAt(Date.now());
				clearTimeout(savedTimeoutRef.current);
				savedTimeoutRef.current = setTimeout(() => setStatusSavedAt(0), 2500);
			})
			.catch(() => setStatusError("Failed to save status update."))
			.finally(() => setStatusSaving(false));
	};

	// Build "Property: Project: Phase" title from available fields
	const projectTitle = (() => {
		const property = (p.name     || "").trim();
		const phase    = (p.phaseName || "").trim();
		let project    = "";
		if (p.bqeParent) {
			const parent  = String(p.bqeParent).trim();
			const prefix  = property ? property + " | " : "";
			const stripped = prefix && parent.startsWith(prefix) ? parent.slice(prefix.length) : parent;
			const pipeIdx  = stripped.lastIndexOf(" | ");
			const candidate = pipeIdx !== -1 ? stripped.slice(pipeIdx + 3) : stripped;
			if (candidate && candidate !== property) project = candidate;
		}
		return [property, project, phase].filter(Boolean).join(": ");
	})();

	const budgetChip = !usage
		? { label: "Not set",     bg: "#f1f3f5", fg: "#6b7280" }
		: pct > 1
			? { label: "Over budget", bg: "#ffebee", fg: "#c62828" }
			: pct >= 0.9
				? { label: "Watching",    bg: "#fff8d6", fg: "#a16207" }
				: { label: "On budget",   bg: "#e8f5e9", fg: "#2e7d32" };

	const fmtDate = iso => {
		if (!iso) return "—";
		const [y, m, d] = iso.split("-");
		return new Date(+y, +m - 1, +d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
	};

	return (
		<div className={`pmProjectRow${last && !open ? " pmProjectRow--last" : ""}`}>
			<div className="pmProjectRow-main" onClick={() => setOpen(o => !o)}>
				<div className="pmProjectRow-info">
					<div className="pmProjectRow-nameLine">
						<span className="pmProjectRow-property">{projectTitle}</span>
					</div>
				</div>

				<div className="pmHealthCol">
					{p.status
						? <span
								className={`pmStatusChip${p.status.trim().toLowerCase() === "on hold" ? " onHold" : ""}`}
							>
								{p.status}
							</span>
						: <span className="pmHealthChip pmHealthChip--muted">—</span>}
				</div>

				<div>
					{awaitingBudget
						? <span className="pmHealthChip pmHealthChip--muted">—</span>
						: (
							<Tooltip title={usage ? budgetUsageText(usage) : ""} arrow placement="top">
								<span className="pmBudgetChip" style={{ background: budgetChip.bg, color: budgetChip.fg }}>
									{budgetChip.label}
								</span>
							</Tooltip>
						)}
				</div>

				<div className="pmHealthCol">
					<AmendmentsChip submittedCount={health.amendment_submitted_count ?? 0} pendingCount={health.amendment_pending_count ?? 0} />
				</div>

				<div className="pmHealthCol">
					<ContactChip hasRecentContact={health.has_recent_contact} />
				</div>

				<div className="pmHealthCol">
					<CompanyCamChip
						hasRecentPhotos={health.has_recent_photos}
						recentPhotoCount={health.recent_photo_count}
						allTimePhotoCount={health.all_time_photo_count}
					/>
				</div>

				<div className="pmHealthCol">
					<TimeEntriesChip hasRecentTimeEntries={health.has_recent_time_entries} />
				</div>

				<div>
					<a
						className="pmLinkBtn"
						href={`/projects/viewer?recordId=${encodeURIComponent(p.id)}`}
						target="_blank"
						rel="noreferrer"
						onClick={e => e.stopPropagation()}
					>
						View <VisibilityIcon style={{ fontSize: 14 }} />
					</a>
				</div>

				<div>
					<Tooltip title="Edit status & schedule" arrow placement="top">
						<button
							type="button"
							className="pmLinkBtn"
							onClick={e => { e.stopPropagation(); setEditDialogOpen(true); }}
						>
							Edit <EditIcon style={{ fontSize: 14 }} />
						</button>
					</Tooltip>
				</div>

				<span className="pmProjectRow-chevron" style={{ transform: open ? "rotate(180deg)" : "none" }}>
					<ExpandMoreIcon style={{ fontSize: 20, color: "#b5bcc6" }} />
				</span>
			</div>

			<StatusScheduleDialog
				open={editDialogOpen}
				onClose={() => setEditDialogOpen(false)}
				projectName={projectTitle}
				currentStatus={String(p.status || "").trim().toLowerCase()}
				rawStart={p.start || ""}
				rawEndDate={p.end || ""}
				onHoldRawStart={p.onHoldStart || ""}
				onHoldRawEndDate={p.onHoldEnd || ""}
				onSaveStatus={handleSaveStatus}
				onSaveSchedule={handleSaveSchedule}
				onSaveOnHoldSchedule={handleSaveOnHoldSchedule}
				onLogFailure={handleLogSaveFailure}
				onCompleted={() => { setEditDialogOpen(false); onCompleted?.(); }}
			/>

			{open && (
				<div className="pmProjectRow-detail">
					{/* Quick links bar */}
					<div className="pmQuickLinksBar">
						{[
							{ key: "hubspot", label: "HubSpot",    abbr: "HS", cls: "hubspot", href: hubspotUrl    },
							{ key: "ccam",    label: "CompanyCam", abbr: "CC", cls: "ccam",    href: companyCamUrl },
							{ key: "bqe",     label: "BQE",        abbr: "BQ", cls: "bqe",     href: bqeUrl        },
							{ key: "dropbox", label: "Dropbox",    abbr: "DB", cls: "dropbox", href: dropboxUrl    },
						].map(link => (
							<a
								key={link.key}
								className={`pmQuickLinkItem${!link.href ? " disabled" : ""}`}
								href={link.href || undefined}
								target="_blank"
								rel="noreferrer"
							>
								<div className={`pmIntLinkBadge ${link.cls}`}>{link.abbr}</div>
								<span className="pmQuickLinkTitle">{link.label}</span>
							</a>
						))}
					</div>
					{/* Details */}
					<div className="pmDetailContent">
						<div className="pmDetailGrid">
							<span className="pmDetailKey">Start</span>
							<span className="pmDetailVal">{fmtDate(p.start)}</span>
							<span className="pmDetailKey">End</span>
							<span className="pmDetailVal">{fmtDate(p.end)}</span>
							<span className="pmDetailKey">Budget</span>
							<span className="pmDetailVal" style={{
								color: !usage ? "#9ca3af" : pct > 1 ? "#c62828" : pct >= 0.9 ? "#a16207" : "#2e7d32",
								fontWeight: usage ? 500 : undefined,
							}}>
								{awaitingBudget ? "Loading…" : usage ? budgetUsageText(usage) : "Not set"}
							</span>
						</div>
					</div>

					{/* Project status update — free-text note synced to the HubSpot deal */}
					<div className="pmStatusUpdate">
						<div className="pmStatusUpdate-header">
							<span className="pmAiOverview-title">Project Status Update</span>
							{statusSaving
								? <span className="pmStatusUpdate-saveState">Saving…</span>
								: statusSavedAt && !isEditingStatus
									? <span className="pmStatusUpdate-saveState">Saved</span>
									: !isEditingStatus && dealId && !statusLoading
										? (
											<Tooltip title="Edit" arrow placement="top">
												<IconButton size="small" sx={{ p: 0.5 }} onClick={startEditingStatus}>
													<EditIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										)
										: null}
						</div>
						{!dealId ? (
							<p className="pmAiOverview-empty">No HubSpot deal linked to this project.</p>
						) : statusLoading ? (
							<p className="pmAiOverview-empty">Loading…</p>
						) : !isEditingStatus ? (
							statusText
								? <p className="pmAiOverview-text">{statusText}</p>
								: <p className="pmAiOverview-empty">No status update yet.</p>
						) : (
							<>
								<textarea
									className="pmStatusUpdate-textarea"
									value={statusDraft}
									onChange={e => setStatusDraft(e.target.value)}
									placeholder="Write a status update for this project…"
									rows={4}
									autoFocus
								/>
								<div className="pmStatusUpdate-footer">
									{statusError && <span className="pmStatusUpdate-error">{statusError}</span>}
									<span style={{ flex: 1 }} />
									<Button
										size="small"
										variant="outlined"
										color="error"
										disabled={statusSaving}
										onClick={cancelEditingStatus}
									>
										Cancel
									</Button>
									<Button
										size="small"
										variant="contained"
										disabled={statusSaving}
										onClick={saveStatusUpdate}
									>
										{statusSaving ? "Saving…" : "Save to HubSpot"}
									</Button>
								</div>
							</>
						)}
					</div>

					{/* AI overview summary — same source + wording as Project Viewer's At A Glance tab */}
					<div className="pmAiOverview">
						<div className="pmAiOverview-header">
							<span className="pmAiOverview-title">AI Overview</span>
							{health.ai_overview_period_start && health.ai_overview_period_end && (
								<span className="pmAiOverview-period">
									Report period: {fmtDate(health.ai_overview_period_start)} – {fmtDate(health.ai_overview_period_end)}
								</span>
							)}
						</div>
						{health.ai_overview_summary
							? (
								<>
									<p className="pmAiOverview-text">{health.ai_overview_summary}</p>
									<div className="pmAiOverview-disclaimer">
										Overview generated using ChatGPT, please double check all information in this section.
									</div>
								</>
							)
							: <p className="pmAiOverview-empty">No automated reports have been generated for this phase yet.</p>}
					</div>
				</div>
			)}
		</div>
	);
}

// ── Action item row ────────────────────────────────────────────────────────────

function ActionRow({ item }) {
	const tones = {
		high: { dot: "#c62828", bg: "#ffebee" },
		med:  { dot: "#ca8a04", bg: "#fffbe9" },
		low:  { dot: "#566472", bg: "#eef1f4" },
	};
	const tone = tones[item.sev] || tones.low;
	return (
		<div className="pmActionRow">
			<span className="pmActionRow-iconWrap" style={{ background: tone.bg, color: tone.dot }}>
				<Icon name={item.icon} style={{ fontSize: 17 }} />
			</span>
			<div className="pmActionRow-text">
				<div className="pmActionRow-title">{item.text}</div>
				<div className="pmActionRow-project">{item.project}</div>
			</div>
			{item.href
				? <a className="pmLinkBtn" href={item.href} target="_blank" rel="noreferrer">{item.cta} <ChevronRightIcon style={{ fontSize: 14 }} /></a>
				: <span className="pmLinkBtn">{item.cta} <ChevronRightIcon style={{ fontSize: 14 }} /></span>
			}
		</div>
	);
}

// ── Budget watch row ───────────────────────────────────────────────────────────

function BudgetRow({ b, finance }) {
	const [expanded, setExpanded] = useState(false);
	const pctNum = Math.round(b.pct * 100);
	const status = b.pct > 1 ? "Over budget" : pctNum >= 90 ? "Near limit" : "On track";

	const invoicedAmount = finance?.total_invoice_amount ?? 0;
	const invoicedBalance = finance?.total_balance ?? 0;
	const overdueAmount = finance?.overdue_balance ?? 0;
	const unpaidNotOverdueAmount = Math.max(invoicedBalance - overdueAmount, 0);
	const paidAmount = Math.max(invoicedAmount - invoicedBalance, 0);
	const budgetedTotal = (finance?.services_amount ?? 0) + (finance?.expense_amount ?? 0);
	const budgetedNotInvoicedAmount = Math.max(budgetedTotal - invoicedAmount, 0);
	const unbudgetedExpenseAmount = finance?.unbudgeted_expense_total ?? 0;
	const contractAmount = finance?.contract_amount ?? null;

	return (
		<div className="pmBudgetRow">
			<div className="pmBudgetRow-header">
				<a
					className="pmBudgetRow-property"
					href={`/projects/viewer?recordId=${encodeURIComponent(b.projectId)}&tab=budget`}
					target="_blank"
					rel="noreferrer"
				>
					{b.property}
				</a>
				<Tooltip arrow title={b.usage ? budgetUsageText(b.usage) : ""}>
					<span style={{ fontSize: 13, fontWeight: 700, color: b.tone, flexShrink: 0 }}>{pctNum}%</span>
				</Tooltip>
			</div>
			<div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
				{contractAmount != null ? (
					<ProgressStackBar
						target={contractAmount}
						formatValue={fmtCurrency}
						segments={[
							{ key: "paid", label: "Invoiced & Paid", value: paidAmount, color: "#2e7d32" },
							{ key: "invoiced_unpaid", label: "Invoiced (Unpaid)", value: unpaidNotOverdueAmount, color: "#1976d2" },
							{ key: "overdue", label: "Invoiced (Overdue)", value: overdueAmount, color: "#c62828" },
							{ key: "budgeted_not_invoiced", label: "Budgeted (Not Invoiced)", value: budgetedNotInvoicedAmount, color: "#81c784" },
							{ key: "not_in_budget_expenses", label: "Not In Budget — Expenses", value: unbudgetedExpenseAmount, color: "#f0a202" },
						]}
					/>
				) : (
					<div style={{ fontSize: 12, color: "#7a8190" }}>{finance ? "No contract amount recorded for this phase." : "Loading finances…"}</div>
				)}
				<button type="button" className="pmBudgetRow-expandToggle" onClick={() => setExpanded(v => !v)}>
					{expanded ? <ExpandLessIcon style={{ fontSize: 18 }} /> : <ExpandMoreIcon style={{ fontSize: 18 }} />}
					{expanded ? "Hide hours" : "Show hours"}
				</button>
				{expanded && (
					<>
						<ProgressStackBar
							title="Hours"
							target={b.total}
							formatValue={fmtHours}
							segments={[
								{ key: "in_budget", label: "Logged (In Budget)", value: b.inBudgetSpent, color: "#2e7d32" },
								{ key: "remaining", label: "Remaining (Budgeted)", value: Math.max(b.total - b.inBudgetSpent, 0), color: "#81c784" },
								{ key: "not_in_budget", label: "Logged (Not In Budget)", value: b.notInBudgetSpent, color: "#b45309" },
							]}
						/>
						<div className="pmBudgetRow-meta">
							<span>{fmtHours(b.spent)}h of {fmtHours(b.total)}h</span>
							<span style={{ color: b.pct > 1 ? "#c62828" : pctNum >= 90 ? "#ca8a04" : "#7a8190", fontWeight: pctNum >= 90 ? 700 : 400 }}>
								{status}
							</span>
						</div>
					</>
				)}
			</div>
		</div>
	);
}

// ── Employee picker ────────────────────────────────────────────────────────────
// employees: array of { id, name, office, ... } from /api/workload/employees
// loggedInId: numeric ID of the logged-in user (from UserContext)
// isAdmin: if true, show all other employees; if false, show only the logged-in user

function EmployeePicker({ selected, onSelect, employees, loggedInId, directReportIds = [], isAdmin }) {
	const [open, setOpen] = useState(false);
	const [q, setQ]       = useState("");
	const ref             = useRef(null);

	useEffect(() => {
		const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, []);

	const matches   = e => !q || (e.name || "").toLowerCase().includes(q.toLowerCase()) || (e.office || "").toLowerCase().includes(q.toLowerCase());
	const reportSet = new Set(directReportIds);

	const me      = employees.filter(e => e.id === loggedInId && matches(e));
	const reports = !isAdmin ? employees.filter(e => reportSet.has(e.id) && matches(e)).sort((a, b) => (a.name || "").localeCompare(b.name || "")) : [];
	const others  = isAdmin  ? employees.filter(e => e.id !== loggedInId && matches(e)).sort((a, b) => (a.name || "").localeCompare(b.name || "")) : [];
	const isEmpty = me.length + reports.length + others.length === 0;

	const EmpRow = ({ e }) => {
		const active = e.id === selected?.id;
		return (
			<button
				className={`pmPickerRow${active ? " pmPickerRow--active" : ""}`}
				onClick={() => { onSelect(e.id); setOpen(false); setQ(""); }}
			>
				<Avatar employee={e} size={32} />
				<span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
					<span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: "rgba(0,0,0,0.85)" }}>{e.name}</span>
					<span style={{ display: "block", fontSize: 11.5, color: "#7a8190" }}>{e.office}</span>
				</span>
				{active && <CheckIcon style={{ fontSize: 18, color: "#184c93" }} />}
			</button>
		);
	};

	const PickerSection = ({ label, people }) => people.length ? (
		<div>
			<div className="pmPickerSection-label">{label}</div>
			{people.map(e => <EmpRow key={e.id} e={e} />)}
		</div>
	) : null;

	return (
		<div ref={ref} style={{ position: "relative" }}>
			<button className="pmPickerTrigger" onClick={() => setOpen(o => !o)}>
				<Avatar employee={selected} size={34} />
				<span style={{ textAlign: "left" }}>
					<span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: "rgba(0,0,0,0.85)" }}>{selected?.name || "…"}</span>
					<span style={{ display: "block", fontSize: 11, color: "#7a8190" }}>{selected?.office || ""}</span>
				</span>
				{open ? <ExpandLessIcon style={{ fontSize: 20, color: "#9ca3af" }} /> : <ExpandMoreIcon style={{ fontSize: 20, color: "#9ca3af" }} />}
			</button>

			{open && (
				<div className="pmPickerDropdown">
					{isAdmin && (
						<div className="pmPickerSearch">
							<div className="pmPickerSearch-inner">
								<SearchIcon style={{ fontSize: 18, color: "#9ca3af" }} />
								<input
									autoFocus
									value={q}
									onChange={e => setQ(e.target.value)}
									placeholder="Search people…"
									className="pmPickerSearchInput"
								/>
							</div>
						</div>
					)}
					<div className="pmPickerList">
						<PickerSection label="YOU" people={me} />
						{!isAdmin && reports.length > 0 && <PickerSection label="DIRECT REPORTS" people={reports} />}
						{isAdmin && <PickerSection label="EMPLOYEES" people={others} />}
						{isEmpty && (
							<div style={{ padding: "20px 12px", textAlign: "center", fontSize: 13, color: "#9ca3af" }}>
								No people match.
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
}

// ── Skeleton loading wireframe ─────────────────────────────────────────────────

function SkeletonBlock({ w, h, r = 4, style }) {
	return <div className="pmSkeleton" style={{ width: w, height: h, borderRadius: r, ...style }} />;
}

function SkeletonSectionCard({ children, wide }) {
	return (
		<div className="pmSectionCard">
			<div className="pmSectionCard-header" style={{ gap: 10 }}>
				<SkeletonBlock w={18} h={18} r={3} />
				<SkeletonBlock h={14} style={{ width: wide ? 160 : 110 }} />
				<SkeletonBlock w={26} h={18} r={999} />
			</div>
			<div className="pmSectionCard-body">{children}</div>
		</div>
	);
}

function SkeletonPage() {
	return (
		<div className="PMProjectDashboard">

			{/* Header */}
			<div className="pmPageHeader">
				<SkeletonBlock h={36} r={6} style={{ width: 260 }} />
				<SkeletonBlock h={50} r={10} style={{ width: 210 }} />
			</div>

			{/* KPI strip */}
			<div className="pmKpiStrip">
				{[0, 1, 2].map(i => (
					<div key={i} className="pmStatCard">
						<div className="pmStatCard-header">
							<SkeletonBlock h={11} r={3} style={{ flex: 1 }} />
							<SkeletonBlock w={28} h={28} r={8} />
						</div>
						<div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
							<SkeletonBlock h={30} r={4} style={{ width: 56 }} />
							<SkeletonBlock h={12} r={3} style={{ width: 36 }} />
						</div>
					</div>
				))}
			</div>

			{/* Body */}
			<div className="pmBody">

				{/* Left column */}
				<div className="pmBodyLeft">

					{/* Workload card */}
					<SkeletonSectionCard wide>
						{/* Segmented control placeholder */}
						<div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
							<SkeletonBlock h={26} r={8} style={{ width: 130 }} />
						</div>
						{/* Chart bars */}
						<div style={{ display: "flex", alignItems: "flex-end", gap: 10, height: 180, paddingTop: 16 }}>
							{[0.55, 0.80, 0.65, 0.90, 0.72, 0.40, 0.60, 0.50].map((pct, i) => (
								<div key={i} className="pmSkeleton" style={{ flex: 1, height: `${pct * 100}%`, borderRadius: "3px 3px 0 0" }} />
							))}
						</div>
						{/* Period labels */}
						<div style={{ display: "flex", gap: 10, marginTop: 6 }}>
							{[0,1,2,3,4,5,6,7].map(i => (
								<SkeletonBlock key={i} h={9} r={2} style={{ flex: 1 }} />
							))}
						</div>
						{/* Legend */}
						<div style={{ display: "flex", gap: 14, marginTop: 12 }}>
							{[60, 80, 70, 50, 58].map((w, i) => (
								<SkeletonBlock key={i} h={10} r={3} style={{ width: w }} />
							))}
						</div>
					</SkeletonSectionCard>

					{/* Project portfolio */}
					<SkeletonSectionCard wide>
						{/* Table head */}
						<div style={{ display: "grid", gridTemplateColumns: "1fr 116px 56px 68px 76px 64px 74px 22px", gap: 12, padding: "8px 0", borderBottom: "2px solid #e3e6eb", marginBottom: 2 }}>
							{[70, 55, 0, 0, 0, 0, 0, 0].map((w, i) => (
								<SkeletonBlock key={i} h={10} r={3} style={{ width: w || undefined }} />
							))}
						</div>
						{/* Project rows */}
						{[0, 1, 2].map(i => (
							<div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 116px 56px 68px 76px 64px 74px 22px", alignItems: "center", gap: 12, padding: "14px 0", borderBottom: i < 2 ? "1px solid #e3e6eb" : "none" }}>
								<div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
									<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
										<SkeletonBlock h={14} r={3} style={{ flex: "0 0 42%" }} />
										<SkeletonBlock h={17} r={999} style={{ width: 90 }} />
									</div>
									<SkeletonBlock h={11} r={3} style={{ width: "55%" }} />
								</div>
								<SkeletonBlock h={22} r={999} style={{ width: 80 }} />
								<SkeletonBlock h={18} r={999} style={{ width: 30, justifySelf: "center" }} />
								<SkeletonBlock h={18} r={999} style={{ width: 44, justifySelf: "center" }} />
								<SkeletonBlock h={18} r={999} style={{ width: 50, justifySelf: "center" }} />
								<SkeletonBlock h={18} r={999} style={{ width: 40, justifySelf: "center" }} />
								<SkeletonBlock h={13} r={3} style={{ width: 46 }} />
								<SkeletonBlock w={18} h={18} r={3} />
							</div>
						))}
					</SkeletonSectionCard>
				</div>

				{/* Right column */}
				<div className="pmBodyRight">

					{/* Action items */}
					<SkeletonSectionCard>
						{[0, 1, 2, 3].map(i => (
							<div key={i} className="pmActionRow" style={{ borderBottom: i < 3 ? "1px solid #e3e6eb" : "none" }}>
								<SkeletonBlock w={30} h={30} r={8} />
								<div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
									<SkeletonBlock h={13} r={3} style={{ width: "78%" }} />
									<SkeletonBlock h={11} r={3} style={{ width: "52%" }} />
								</div>
								<SkeletonBlock h={13} r={3} style={{ width: 52 }} />
							</div>
						))}
					</SkeletonSectionCard>

					{/* Budget watch */}
					<SkeletonSectionCard>
						{[0, 1, 2].map(i => (
							<div key={i} className="pmBudgetRow" style={{ borderBottom: i < 2 ? "1px solid #e3e6eb" : "none" }}>
								<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
									<SkeletonBlock h={13} r={3} style={{ width: "62%" }} />
									<SkeletonBlock h={13} r={3} style={{ width: 30 }} />
								</div>
								<SkeletonBlock h={6} r={999} style={{ width: "100%", marginBottom: 6 }} />
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<SkeletonBlock h={11} r={3} style={{ width: "42%" }} />
									<SkeletonBlock h={11} r={3} style={{ width: 52 }} />
								</div>
							</div>
						))}
					</SkeletonSectionCard>
				</div>
			</div>
		</div>
	);
}

// ── Page tabs ──────────────────────────────────────────────────────────────────

const PAGE_TABS = [
	{ id: "overview", label: "Overview" },
	{ id: "contacts", label: "My Contacts" },
];

function PageTabs({ activeId, onChange }) {
	return (
		<div className="pmPageTabs">
			{PAGE_TABS.map((t) => (
				<button
					key={t.id}
					type="button"
					className={`pmPageTab${activeId === t.id ? " pmPageTab--active" : ""}`}
					onClick={() => onChange(t.id)}
				>
					{t.label}
				</button>
			))}
		</div>
	);
}

// ── Main component ─────────────────────────────────────────────────────────────

const PMProjectDashboard = ({ isAdmin = false }) => {
	const { employeeId: urlEmployeeId } = useParams();
	const navigate   = useNavigate();
	const { instance, accounts } = useMsal();
	const [searchParams, setSearchParams] = useSearchParams();
	const activeTab = PAGE_TABS.some(t => t.id === searchParams.get("tab")) ? searchParams.get("tab") : "overview";
	const setActiveTab = (id) => {
		setSearchParams(prev => {
			const next = new URLSearchParams(prev);
			if (id === "overview") next.delete("tab"); else next.set("tab", id);
			return next;
		});
	};
	const loggedInId = useContext(UserContext); // numeric employee ID of the logged-in user

	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "PM Project Dashboard",
			employeeID: loggedInId,
			notes: "Accessed the PM Project Dashboard page",
		}).catch(() => {});
	}, []);

	const [employees, setEmployees]             = useState([]);
	const [selId, setSelId]                     = useState(null);
	const [rootId, setRootId]                   = useState(null); // effective "logged-in" identity, captured once on load
	const [loadingEmps, setLoadingEmps]         = useState(true);
	const [directReportIds, setDirectReportIds] = useState([]);
	const [loadingReports, setLoadingReports]   = useState(false);
	const [timeEntries, setTimeEntries]         = useState([]);
	const [budgets, setBudgets]                 = useState([]);
	const [spentHours, setSpentHours]           = useState([]);
	const [budgetFinances, setBudgetFinances]   = useState({}); // project_id -> finance figures, from /api/workload/budget-finances
	const [loadingData, setLoadingData]         = useState(true);
	const [grain, setGrain]                     = useState("week");
	const [hubspotStatus, setHubspotStatus]     = useState({});
	const [healthIndicators, setHealthIndicators] = useState({}); // bqe_id -> amendment counts + budget $ status
	const [healthLoaded, setHealthLoaded]         = useState(false); // budget status waits on this
	const [healthActivity, setHealthActivity]     = useState({}); // bqe_id -> contact/CompanyCam/time-entry activity
	const [timeOffMap, setTimeOffMap]           = useState({});
	const [holidayList, setHolidayList]         = useState([]);

	// Fetch all employees once on mount
	useEffect(() => {
		const fetch = async () => {
			try {
				const res = await getAPICall(instance, accounts[0], "/api/workload/employees");
				setEmployees(res.data || []);
			} catch (err) {
				console.error("[PMDashboard] employees fetch failed", err);
			} finally {
				setLoadingEmps(false);
			}
		};
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Fetch time entries (5 months back → 3 months forward) and budgets in parallel.
	// Both are fetched once; KPIs and workload series are derived client-side per employee.
	useEffect(() => {
		const fetch = async () => {
			const now  = new Date();
			const fromRaw = addMonths(now, -5);
			const from    = toDateStr(new Date(fromRaw.getFullYear(), fromRaw.getMonth(), 1));
			const to      = toDateStr(addMonths(now, 3));
			try {
				const [teRes, bRes, shRes] = await Promise.all([
					getAPICall(instance, accounts[0], "/api/workload/time-entries", { from, to }),
					getAPICall(instance, accounts[0], "/api/workload/budgets"),
					getAPICall(instance, accounts[0], "/api/workload/spent-hours"),
				]);
				setTimeEntries(teRes.data?.entries || []);
				setBudgets(bRes.data?.budgets || []);
				setSpentHours(shRes.data?.spent || []);
			} catch (err) {
				console.error("[PMDashboard] workload data fetch failed", err);
			} finally {
				setLoadingData(false);
			}
		};
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Fetch project health indicators (amendment counts) once — same source (and same
	// numbers) as the Project Health Dashboard's Amendments column.
	useEffect(() => {
		getAPICall(instance, accounts[0], "/api/project-health/indicators")
			.then(res => {
				const map = {};
				(res.data?.indicators || []).forEach(row => { if (row.bqe_id) map[row.bqe_id] = row; });
				setHealthIndicators(map);
			})
			.catch(err => console.error("[PMDashboard] health indicators fetch failed", err))
			.finally(() => setHealthLoaded(true));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Fetch project health activity (HubSpot contact / CompanyCam / time entries) —
	// same cache the Project Health Dashboard reads, refreshed hourly on the server.
	useEffect(() => {
		let cancelled = false;
		const fetchActivity = () => {
			getAPICall(instance, accounts[0], "/api/project-health/activity")
				.then(res => {
					if (cancelled || !Array.isArray(res.data?.activity)) return;
					const map = {};
					res.data.activity.forEach(row => { if (row.bqe_id) map[row.bqe_id] = row; });
					setHealthActivity(map);
				})
				.catch(err => console.error("[PMDashboard] health activity fetch failed", err));
		};
		fetchActivity();
		const interval = setInterval(fetchActivity, 60 * 60 * 1000);
		return () => { cancelled = true; clearInterval(interval); };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Fetch direct reports for rootId (the effective identity set on load).
	// Stays stable — does not re-fetch as selId changes while navigating.
	useEffect(() => {
		if (!rootId) return;
		setLoadingReports(true);
		const fetch = async () => {
			try {
				const res = await getAPICall(instance, accounts[0], `/api/bqe/reportees/${rootId}`);
				const reports = res.data?.reports || [];
				setDirectReportIds(reports.map(r => r.id));
			} catch (err) {
				console.error("[PMDashboard] reportees fetch failed", err);
				setDirectReportIds([]);
			} finally {
				setLoadingReports(false);
			}
		};
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rootId]);

	// Capture the effective identity once: URL param wins over the context user.
	// rootId is stable for the lifetime of the component — it drives the picker's
	// "YOU" section, the direct-reports fetch, and the initial page selection.
	// selId then changes independently as the user navigates between employees.
	useEffect(() => {
		if (selId !== null || employees.length === 0) return;
		const urlId = urlEmployeeId ? parseInt(urlEmployeeId, 10) : null;
		const target = (urlId && employees.some(e => e.id === urlId)) ? urlId : loggedInId;
		if (target) {
			setSelId(target);
			setRootId(target);
		}
	}, [employees, selId, urlEmployeeId, loggedInId]);

	const selectedEmp = employees.find(e => e.id === selId) || null;

	// Log page access whenever the viewed employee changes
	useEffect(() => {
		if (!selId || employees.length === 0) return;
		const viewed = employees.find(e => e.id === selId);
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name:   "PM Project Dashboard",
			employeeID:  loggedInId,
			notes:       `Viewing ${viewed?.name || `employee ${selId}`}`,
		}).catch(() => {});
	}, [selId, employees, loggedInId]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleSelect = (id) => {
		setSelId(id);
		navigate(`/projects/pm-dashboard/${id}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`);
	};

	const [portfolioTab, setPortfolioTab] = useState("owner");
	const [showOnHoldBudget, setShowOnHoldBudget] = useState(false);

	// Local patches applied on top of the last server fetch so a status/schedule edit
	// is reflected immediately, without waiting on the next timeEntries/budgets refresh.
	const [projectOverrides, setProjectOverrides] = useState({});
	const [hiddenProjectIds, setHiddenProjectIds]  = useState(() => new Set());
	const handleProjectUpdated = (id, patch) => {
		setProjectOverrides(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
	};
	const handleProjectCompleted = (id) => {
		setHiddenProjectIds(prev => new Set(prev).add(id));
	};

	// Shared by all four compute* calls below — was independently rebuilt inside
	// each one (4 redundant O(n) passes over timeEntries+budgets on every data
	// change); built once here instead.
	const projRegistry         = useMemo(() => buildProjectRegistry(timeEntries, budgets), [timeEntries, budgets]);
	const kpis                 = useMemo(() => computeKPIs(timeEntries, budgets, selectedEmp, projRegistry, timeOffMap, holidayList), [timeEntries, budgets, selectedEmp, projRegistry, timeOffMap, holidayList]);
	const series               = useMemo(() => buildWorkloadSeries(timeEntries, budgets, selectedEmp, grain, timeOffMap, holidayList, spentHours), [timeEntries, budgets, selectedEmp, grain, timeOffMap, holidayList, spentHours]);
	const budgetWatch          = useMemo(() => computeBudgetWatch(timeEntries, budgets, spentHours, selectedEmp, projRegistry, healthIndicators), [timeEntries, budgets, spentHours, selectedEmp, projRegistry, healthIndicators]);

	// Fetch dollar figures for exactly the phases Budget Watch is showing, once
	// per distinct set of phases — batched in one call rather than one per row.
	const budgetWatchProjectIdsKey = useMemo(
		() => [...new Set([...budgetWatch.active, ...budgetWatch.onHold].map(b => b.projectId))].sort().join(","),
		[budgetWatch]
	);
	useEffect(() => {
		if (!budgetWatchProjectIdsKey) { setBudgetFinances({}); return; }
		let cancelled = false;
		getAPICall(instance, accounts[0], "/api/workload/budget-finances", { projectIds: budgetWatchProjectIdsKey })
			.then(res => {
				if (cancelled) return;
				const map = {};
				(res.data?.finances || []).forEach(f => { map[f.project_id] = f; });
				setBudgetFinances(map);
			})
			.catch(err => console.error("[PMDashboard] budget-finances fetch failed", err));
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [budgetWatchProjectIdsKey]);
	const portfolio            = useMemo(() => computeProjectPortfolio(timeEntries, budgets, spentHours, selectedEmp, projRegistry),     [timeEntries, budgets, spentHours, selectedEmp, projRegistry]);
	const contributorPortfolio = useMemo(() => computeContributorProjects(timeEntries, budgets, spentHours, selectedEmp, projRegistry),  [timeEntries, budgets, spentHours, selectedEmp, projRegistry]);

	const applyProjectOverrides = (list) => list
		.filter(proj => !hiddenProjectIds.has(proj.id))
		.map(proj => projectOverrides[proj.id] ? { ...proj, ...projectOverrides[proj.id] } : proj);

	const activePortfolio = useMemo(
		() => applyProjectOverrides(portfolioTab === "owner" ? portfolio : contributorPortfolio),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[portfolio, contributorPortfolio, portfolioTab, projectOverrides, hiddenProjectIds],
	);

	// Fetch HubSpot contact status for owned + contributor projects combined
	const portfolioIds = [...new Set([...portfolio.map(p => p.id), ...contributorPortfolio.map(p => p.id)])].join(",");
	useEffect(() => {
		if (!portfolioIds) { setHubspotStatus({}); return; }
		getAPICall(instance, accounts[0], `/api/phaseReports/contactStatus?bqeIds=${encodeURIComponent(portfolioIds)}`)
			.then(res => setHubspotStatus(res.data || {}))
			.catch(() => setHubspotStatus({}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [portfolioIds]);

	// Fetch BambooHR time-off and holidays for the selected employee
	useEffect(() => {
		if (!selectedEmp) { setTimeOffMap({}); setHolidayList([]); return; }
		let cancelled = false;
		const now   = new Date();
		const start = toDateStr(new Date(now.getFullYear() - 1, 0, 1));
		const end   = toDateStr(new Date(now.getFullYear() + 1, 11, 31));
		postAPICall(instance, accounts[0], "/api/bamboohr/schedule_time_off", {
			names: [selectedEmp.name],
			start,
			end,
		})
			.then(res => {
				if (!cancelled) {
					setTimeOffMap(res.data?.timeOff || {});
					setHolidayList(res.data?.holidays || []);
				}
			})
			.catch(err => { if (!cancelled) console.error("[PMDashboard] bamboohr fetch failed", err); });
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedEmp?.id]);

	const actions = useMemo(() => computeActionItems(portfolio, hubspotStatus), [portfolio, hubspotStatus]);

	if (loadingEmps || loadingData) {
		return <SkeletonPage />;
	}

	return (
		<div className="PMProjectDashboard">

			{/* Header */}
			<div className="pmPageHeader">
				<h1 className="pmTitle">PM Project Dashboard</h1>
				<EmployeePicker
					selected={selectedEmp}
					onSelect={handleSelect}
					employees={employees}
					loggedInId={loggedInId}
					directReportIds={directReportIds}
					isAdmin={isAdmin}
				/>
			</div>

			<PageTabs activeId={activeTab} onChange={setActiveTab} />

			<div className="pmTabContent">
			{activeTab === "overview" ? (
				<>
					{/* KPI strip */}
					<div className="pmKpiStrip">
						<StatCard accent tone="blue" tone2="orange"                            icon="folder_open"            label="Projects"
							value={String(kpis.activeCount)} sub="active"
							value2={String(kpis.onHoldCount)} sub2="on hold"
						/>
						<StatCard accent
							tone={kpis.billablePct >= 70 ? "green" : "orange"}
							tone2={kpis.billablePctMTD >= 70 ? "green" : "orange"}
							icon="trending_up" label="Billable"
							value={`${kpis.billablePct}%`}    sub={kpis.billableMonthLabel}
							value2={`${kpis.billablePctMTD}%`} sub2={kpis.billableMTDLabel}
						/>
						<StatCard accent tone={kpis.missingBudget > 0 ? "red" : "green"}      icon="account_balance_wallet" label="Missing Budgets"  value={String(kpis.missingBudget)} sub="not set" />
					</div>

					{/* Body */}
					<div className="pmBody">

						{/* Left column */}
						<div className="pmBodyLeft">
							<SectionCard
								title="Workload"
								icon="work_history"
								titleAfter={
									<a href="/workload/viewer" target="_blank" rel="noreferrer" className="pmWorkloadLink">
										<OpenInNewIcon style={{ fontSize: 14, verticalAlign: "middle" }} />
									</a>
								}
								action={<Segmented value={grain} options={["Day", "Week", "Month"]} onChange={setGrain} />}
							>
								<WorkloadChart series={series} grain={grain} />
							</SectionCard>

							<SectionCard
								title="Project Portfolio"
								icon="visibility"
								count={activePortfolio.length}
								action={<Segmented value={portfolioTab} options={["Owner", "Contributor"]} onChange={setPortfolioTab} />}
							>
								<div className="pmTableHead">
									{PORTFOLIO_HEADERS.map((h, i) => {
										const cell = (
											<span className="pmTableHeadCell" style={h.center ? { textAlign: "center" } : undefined}>
												{h.label}
											</span>
										);
										return (
											<React.Fragment key={i}>
												{h.tooltip
													? <Tooltip title={h.tooltip} arrow placement="top">{cell}</Tooltip>
													: cell}
											</React.Fragment>
										);
									})}
								</div>
								{activePortfolio.length
									? activePortfolio.map((p, i) => (
										<ProjectRow
											key={p.id}
											p={p}
											hs={hubspotStatus[p.id] || {}}
											health={{ ...healthIndicators[p.id], ...healthActivity[p.id] }}
											budgetLoading={!healthLoaded}
											last={i === activePortfolio.length - 1}
											onUpdated={patch => handleProjectUpdated(p.id, patch)}
											onCompleted={() => handleProjectCompleted(p.id)}
										/>
									))
									: <div className="pmEmpty">No active or on-hold {portfolioTab === "owner" ? "owned" : "contributor"} projects.</div>}
							</SectionCard>
						</div>

						{/* Right column */}
						<div className="pmBodyRight">
							<SectionCard title="Action Items" icon="checklist" count={actions.length} bodyStyle={{ maxHeight: 310, overflowY: "auto" }}>
								{actions.length
									? actions.map((a, i) => <ActionRow key={i} item={a} />)
									: (
										<div className="pmAllClear">
											<CheckCircleIcon style={{ fontSize: 18 }} />
											All clear — nothing needs attention.
										</div>
									)}
							</SectionCard>

							<SectionCard
								title="Budget Watch"
								icon="account_balance_wallet"
								count={budgetWatch.active.length}
								action={<span className="pmMuted">% spent</span>}
							>
								{!healthLoaded
									? [0, 1, 2].map(i => (
										<div key={i} className="pmBudgetRow">
											<SkeletonBlock h={13} style={{ width: "70%" }} />
											<SkeletonBlock h={10} style={{ width: "100%", marginTop: 10 }} />
										</div>
									))
									: budgetWatch.active.length
										? budgetWatch.active.map((b, i) => <BudgetRow key={i} b={b} finance={budgetFinances[b.projectId]} />)
										: <div className="pmEmpty">No budgeted projects.</div>}

								{healthLoaded && budgetWatch.onHold.length > 0 && (
									<div style={{ marginTop: 12, borderTop: "1px solid #eef0f3", paddingTop: 8 }}>
										<div
											onClick={() => setShowOnHoldBudget(v => !v)}
											style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", userSelect: "none" }}
										>
											{showOnHoldBudget ? <ExpandLessIcon style={{ fontSize: 18, color: "#7a8190" }} /> : <ExpandMoreIcon style={{ fontSize: 18, color: "#7a8190" }} />}
											<span style={{ fontSize: 13, fontWeight: 600, color: "#586174" }}>
												On Hold ({budgetWatch.onHold.length})
											</span>
										</div>
										{showOnHoldBudget && (
											<div style={{ marginTop: 8 }}>
												{budgetWatch.onHold.map((b, i) => <BudgetRow key={i} b={b} finance={budgetFinances[b.projectId]} />)}
											</div>
										)}
									</div>
								)}
							</SectionCard>
						</div>
					</div>
				</>
			) : (
				<ContactsCleanupTab employee={selectedEmp} bqeIds={portfolioIds} />
			)}
			</div>
		</div>
	);
};

export default PMProjectDashboard;
